import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Search, MoreVertical, TrendingUp, TrendingDown, FolderOpen, User, Calendar, Pencil, Eye, EyeOff, ArrowRight, FileText, Clock, AlertCircle, CheckCircle2, PauseCircle, ChevronDown, ChevronRight, ArrowUpDown, X, Phone, Mail, Ruler, Upload, File, Pin, PinOff, Bookmark, Layers, Link2, Bell, ChevronLeft, Package, Download, Crown, Zap } from 'lucide-react';
const EstimateScreen = React.lazy(() => import('./EstimateScreen').then(m => ({ default: m.EstimateScreen })));
import { hideSpinnersStyle, UNITS } from './constants';
import { DirectoryModal } from './DirectoryModal';
import LandingPage from './LandingPage';
import ClientView from './ClientView';
import ClientViewV2 from './ClientViewV2';
import ContractorView from './ContractorView';
import DirectoryPublicView from './DirectoryPublicView';
import { supabase } from './supabaseClient';
import { trackEvent } from './analytics';
import { uploadFile, deleteFile, getDownloadUrl, getFileBlobUrl } from './googleDrive';
import { uploadFileToSupabase, deleteFileFromSupabase, checkProjectFileLimit } from './supabaseStorage';
import ImportModal from './ImportModal';
import MaterialCalculator from './MaterialCalculator';
import DrawingReader from './DrawingReader';
import { useSubscription } from './useSubscription';
import PaywallModal from './PaywallModal';
import { AdvisorFAB, AdvisorChat, AdvisorAnnouncement } from './LihtarAdvisor';
import OrderViewModal from './OrderViewModal';
import PatchNoteModal from './PatchNoteModal';
import { CustomUnitsContext } from './UnitPicker';
import { useActiveSession, ActiveSessionContext } from './useActiveSession';
import { PassiveBanner, ActiveSessionModals, ReadOnlyWrapper } from './ActiveSessionUI';

// ==================== UTILITY FUNCTIONS ====================

const generateId = () => Date.now() + Math.random();

// Створення нового об'єкта
const createNewProject = (data = {}) => {
  const preliminaryId = generateId();
  const firstEstimateId = generateId();
  const now = new Date().toISOString();
  
  return {
    id: generateId(),
    name: data.name || 'Новий об\'єкт',
    address: data.address || '',
    objectType: data.objectType || '',
    area: data.area || null,
    // Дизайнер
    designerName: data.designerName || '',
    designerPhone: data.designerPhone || '',
    designerEmail: data.designerEmail || '',
    // Замовник
    clientName: data.clientName || '',
    clientPhone: data.clientPhone || '',
    clientEmail: data.clientEmail || '',
    // Партнерська винагорода
    partnerEnabled: data.partnerEnabled || false,
    partnerPercent: data.partnerPercent || 10,
    partnerBase: data.partnerBase || 'workProfit', // 'workProfit' | 'workCost' | 'totalProfit'
    partnerName: data.partnerName || '',
    // Документи
    projectPdf: data.projectPdf || null,
    contractFile: data.contractFile || null,
    // Системні
    startDate: now.split('T')[0],
    updatedAt: now,
    pinned: false,
    status: 'active',
    estimates: [
      {
        id: preliminaryId,
        name: 'Попередній',
        isPreliminary: true,
        items: data.templateItems || [], // Позиції з шаблонів
        payments: [],
        atsEnabled: true,
        atsPercent: 15
      },
      {
        id: firstEstimateId,
        name: 'Звіт №1',
        isPreliminary: false,
        items: [],
        payments: [],
        includePreviousBalance: true,
        atsEnabled: true,
        atsPercent: 15
      }
    ],
    activeEstimateId: firstEstimateId
  };
};

// Типи об'єктів
const OBJECT_TYPES = [
  { key: 'apartment', label: 'Квартира', icon: '🏢' },
  { key: 'house', label: 'Будинок', icon: '🏠' },
  { key: 'duplex', label: 'Дуплекс', icon: '🏘️' },
  { key: 'townhouse', label: 'Таунхаус', icon: '🏡' },
  { key: 'commercial', label: 'Комерція', icon: '🏪' },
  { key: 'other', label: 'Інше', icon: '📍' }
];

const getObjectTypeLabel = (key) => {
  const type = OBJECT_TYPES.find(t => t.key === key);
  return type ? type.label : '';
};

// ==================== PROJECTS PAGE ====================

const ProjectsPage = ({ projects, onSelectProject, onCreateProject, onDeleteProject, onUpdateProject, templates, setTemplates, publicTemplates, sortedPublicTemplates, favTemplates, toggleFavTemplate, filledCards, setFilledCards, orderCards, setOrderCards, user, onLogout, isPro, isTrial, trialDaysLeft, showPaywall, paywallOpen, setPaywallOpen, paywallMessage, trialUsed }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Перемикання між об'єктами і шаблонами
  const [currentView, setCurrentView] = useState('projects'); // 'projects' | 'templates'
  
  // Модалка довідника
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);

  // Брендинг — state з Supabase як source of truth, LS як first-paint кеш
  const [brandSettings, setBrandSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lihtar_brand_settings') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await supabase.from('user_brand_settings').select('settings').eq('user_id', user.id).single();
        if (data?.settings) {
          setBrandSettings(data.settings);
          localStorage.setItem('lihtar_brand_settings', JSON.stringify(data.settings));
        }
      } catch (e) { /* нема брендингу — залишаємо кеш або {} */ }
    })();
  }, [user?.id]);
  
  // Фільтр для ціни за м² ('all' | 'work' | 'materials')
  const [pricePerSqmFilter, setPricePerSqmFilter] = useState('all');
  
  // Фільтр по типу об'єкта
  const [objectTypeFilter, setObjectTypeFilter] = useState('all');
  
  // Модалка підтвердження (замість window.confirm)
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: null, cancelText: null, confirmStyle: null });
  
  // Share модалка
  const [shareModalProject, setShareModalProject] = useState(null);
  const [shareModalData, setShareModalData] = useState(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [projectShareStatuses, setProjectShareStatuses] = useState({});
  const [projectUnreadCounts, setProjectUnreadCounts] = useState({});
  
  // Шаблони (отримуємо через props)
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateViewOnly, setTemplateViewOnly] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [lihtarCategoryFilter, setLihtarCategoryFilter] = useState('Всі');
  const [templateActiveGroup, setTemplateActiveGroup] = useState('templates'); // 'templates' | 'contracts' | 'orders'
  const [showContractTemplateSelect, setShowContractTemplateSelect] = useState(false);
  const [showPublicInTender, setShowPublicInTender] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // Модалка редагування замовлення
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showMergeOrderModal, setShowMergeOrderModal] = useState(false);
  const [mergeOrderName, setMergeOrderName] = useState('');
  const [showTemplateSelectForTender, setShowTemplateSelectForTender] = useState(false); // Модалка вибору шаблону для картки підряду
  const [showTemplateImportModal, setShowTemplateImportModal] = useState(false); // Модалка імпорту на сторінці шаблонів
  const [showCalculator, setShowCalculator] = useState(false); // Калькулятор матеріалів
  const [viewingContract, setViewingContract] = useState(null); // Перегляд картки підряду
  const [contractResponses, setContractResponses] = useState([]); // Відповіді підрядників
  const [expandedContractor, setExpandedContractor] = useState(null); // Розгорнутий підрядник
  const [contractViewLoading, setContractViewLoading] = useState(false);
  const [templateExpandedSections, setTemplateExpandedSections] = useState([]);
  
  // Модалки для шаблону (як в довіднику)
  const [showTemplateAddSection, setShowTemplateAddSection] = useState(false);
  const [newTemplateSectionName, setNewTemplateSectionName] = useState('');
  const [showTemplateAddItem, setShowTemplateAddItem] = useState(false);
  const [addItemToTemplateSectionIdx, setAddItemToTemplateSectionIdx] = useState(null);
  const [newTemplateItem, setNewTemplateItem] = useState({ position: '', unit: 'м²', price: '', type: 'Робота' });
  const [showTemplateEditItem, setShowTemplateEditItem] = useState(false);
  const [editTemplateItem, setEditTemplateItem] = useState({ sectionIdx: null, itemIdx: null, position: '', unit: 'м²', price: '', type: 'Робота' });
  
  // Модалка нового об'єкта
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  
  // Покроковий тур
  const [tourStep, setTourStep] = useState(() => {
    try { return localStorage.getItem('lihtar_tour_projects') === 'done' ? 0 : -1; } catch { return -1; }
  }); // -1 = not started, 0 = done, 1-5 = active step
  const [tourRect, setTourRect] = useState(null);
  
  // Оновлення позиції елемента туру
  React.useEffect(() => {
    if (tourStep <= 0) { setTourRect(null); return; }
    const tourTargets = ['project-card', 'editor-button', 'fab-directory', 'fab-templates', 'fab-add'];
    const target = tourTargets[tourStep - 1];
    if (!target) return;
    
    const updateRect = () => {
      const el = document.querySelector(`[data-tour="${target}"]`);
      if (el) setTourRect(el.getBoundingClientRect());
    };
    
    updateRect();
    const interval = setInterval(updateRect, 200);
    window.addEventListener('resize', updateRect);
    return () => { clearInterval(interval); window.removeEventListener('resize', updateRect); };
  }, [tourStep, expandedProjectId]);
  const [newProjectData, setNewProjectData] = useState({
    name: '',
    address: '',
    objectType: '',
    area: '',
    designerName: '',
    designerPhone: '',
    designerEmail: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    projectPdf: null,
    contractFile: null,
    selectedTemplateIds: [] // Вибрані шаблони при створенні
  });
  
  // Універсальна модалка перегляду файлів
  const [previewFile, setPreviewFile] = useState(null);

  const resetNewProjectForm = () => {
    setNewProjectData({
      name: '',
      address: '',
      objectType: '',
      area: '',
      designerName: '',
      designerPhone: '',
      designerEmail: '',
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      projectPdf: null,
      contractFile: null,
      selectedTemplateIds: []
    });
  };

  // === Share функції ===
  useEffect(() => {
    if (!user) return;
    const loadShareStatuses = async () => {
      try {
        const { data } = await supabase
          .from('shared_projects')
          .select('project_id, share_token, is_locked, show_preliminary, is_active')
          .eq('user_id', user.id);
        if (data) {
          const statuses = {};
          data.forEach(s => { statuses[s.project_id] = s; });
          setProjectShareStatuses(statuses);
        }
      } catch (e) { /* ignore */ }
    };
    loadShareStatuses();
  }, [user]);

  // Каунтер непрочитаних per-project
  useEffect(() => {
    if (!user) return;
    const loadUnread = async () => {
      try {
        const { data: shares } = await supabase
          .from('shared_projects')
          .select('share_token, project_id')
          .eq('user_id', user.id);
        if (!shares || shares.length === 0) return;
        
        const tokens = shares.map(s => s.share_token);
        const { data: unreadMessages } = await supabase
          .from('share_comments')
          .select('share_token')
          .in('share_token', tokens)
          .eq('is_from_client', true)
          .eq('is_read', false);
        
        // Групуємо по project_id
        const counts = {};
        (unreadMessages || []).forEach(msg => {
          const share = shares.find(s => s.share_token === msg.share_token);
          if (share) {
            counts[share.project_id] = (counts[share.project_id] || 0) + 1;
          }
        });
        setProjectUnreadCounts(counts);
      } catch (e) { /* ignore */ }
    };
    loadUnread();
    const interval = setInterval(loadUnread, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const openShareModal = async (project) => {
    setShareModalProject(project);
    setShareModalLoading(true);
    try {
      const { data: existing } = await supabase
        .from('shared_projects')
        .select('*')
        .eq('user_id', user.id)
        .eq('project_id', String(project.id))
        .single();
      
      if (existing) {
        setShareModalData(existing);
        // Оновлюємо brand_settings при кожному відкритті
        try {
          const bs = brandSettings && Object.keys(brandSettings).length > 0 ? brandSettings : null;
          const brandForShare = bs ? {
            companyName: bs.companyName || '',
            managerName: bs.managerName || '',
            phone: bs.phone || '',
            email: bs.email || '',
            website: bs.website || '',
            logo: bs.logoCropped || bs.logoRaw || null,
            primaryColor: bs.primaryColor || '#163248',
            sectionBg: bs.sectionBg || '#fde68a',
            atsBg: bs.atsBg || '#fef3c7'
          } : null;
          await supabase.from('shared_projects').update({ brand_settings: brandForShare }).eq('id', existing.id);
        } catch (e) { /* ignore brand update errors */ }
      } else {
        // Створюємо нове
        // Зчитуємо brand settings для посилання клієнта
        const bs = brandSettings && Object.keys(brandSettings).length > 0 ? brandSettings : null;
        const brandForShare = bs ? {
          companyName: bs.companyName || '',
          managerName: bs.managerName || '',
          phone: bs.phone || '',
          email: bs.email || '',
          website: bs.website || '',
          logo: bs.logoCropped || bs.logoRaw || null,
          primaryColor: bs.primaryColor || '#163248',
          sectionBg: bs.sectionBg || '#fde68a',
          atsBg: bs.atsBg || '#fef3c7'
        } : null;

        const { data: newShare, error } = await supabase
          .from('shared_projects')
          .insert({
            user_id: user.id,
            project_id: String(project.id),
            is_locked: false,
            show_preliminary: false,
            is_active: true,
            brand_settings: brandForShare
          })
          .select()
          .single();
        if (error) throw error;
        setShareModalData(newShare);
        setProjectShareStatuses(prev => ({ ...prev, [String(project.id)]: newShare }));
      }
    } catch (e) {
      console.error('Share error:', e);
    }
    setShareModalLoading(false);
  };

  const updateShareField = async (field, value) => {
    if (!shareModalData) return;
    try {
      const update = { [field]: value, updated_at: new Date().toISOString() };
      
      // Якщо lock — зберігаємо snapshot
      if (field === 'is_locked' && value === true) {
        const projectData = projects.find(p => String(p.id) === String(shareModalData.project_id));
        if (projectData) update.locked_snapshot = projectData;
      }
      if (field === 'is_locked' && value === false) {
        update.locked_snapshot = null;
      }

      const { data, error } = await supabase
        .from('shared_projects')
        .update(update)
        .eq('id', shareModalData.id)
        .select()
        .single();
      if (error) throw error;
      setShareModalData(data);
      setProjectShareStatuses(prev => ({ ...prev, [String(data.project_id)]: data }));
    } catch (e) {
      console.error('Update share error:', e);
    }
  };

  const deleteShare = async () => {
    if (!shareModalData) return;
    try {
      await supabase.from('shared_projects').delete().eq('id', shareModalData.id);
      setProjectShareStatuses(prev => {
        const next = { ...prev };
        delete next[String(shareModalData.project_id)];
        return next;
      });
      setShareModalData(null);
      setShareModalProject(null);
    } catch (e) {
      console.error('Delete share error:', e);
    }
  };

  const [shareCopied, setShareCopied] = useState(false);
  const copyShareLink = (token) => {
    const link = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(link);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  // Гарячі клавіші
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                             activeElement?.tagName === 'TEXTAREA' || 
                             activeElement?.isContentEditable;
      
      // Enter - закриває поле вводу (blur)
      if (e.key === 'Enter' && isInputFocused && activeElement?.tagName === 'INPUT') {
        if (!activeElement.classList.contains('no-blur-on-enter')) {
          activeElement.blur();
        }
      }
      
      // Escape - закриває модалки
      if (e.key === 'Escape') {
        if (confirmModal.show) {
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        } else if (showTemplateEditItem) {
          setShowTemplateEditItem(false);
        } else if (showTemplateAddItem) {
          setShowTemplateAddItem(false);
        } else if (showTemplateAddSection) {
          setShowTemplateAddSection(false);
        } else if (showTemplateModal) {
          setShowTemplateModal(false);
          setEditingTemplate(null);
          setTemplateViewOnly(false);
        } else if (previewFile) {
          setPreviewFile(null);
        } else if (showNewProjectModal) {
          setShowNewProjectModal(false);
          setEditingProjectId(null);
          resetNewProjectForm();
        } else if (showDirectoryModal) {
          setShowDirectoryModal(false);
        } else if (menuOpenId) {
          setMenuOpenId(null);
        } else if (showSortMenu) {
          setShowSortMenu(false);
        }
        return;
      }
      
      // Решта клавіш не працює якщо фокус в інпуті
      if (isInputFocused) return;
      
      // Ctrl/Cmd + K - відкрити довідник
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowDirectoryModal(true);
        return;
      }
      
      // Ctrl/Cmd + N - новий об'єкт або шаблон
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        if (currentView === 'templates') {
          setEditingTemplate({ id: null, name: '', description: '', sections: [] });
          setTemplateExpandedSections([]);
          setTemplateViewOnly(false);
          setShowTemplateModal(true);
        } else {
          setShowNewProjectModal(true);
        }
        return;
      }
      
      // Ctrl/Cmd + F або / - фокус на пошук
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Пошук"]');
        if (searchInput) searchInput.focus();
        return;
      }
      
      if (e.key === '/') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Пошук"]');
        if (searchInput) searchInput.focus();
        return;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmModal, showTemplateEditItem, showTemplateAddItem, showTemplateAddSection, 
      showTemplateModal, previewFile, showNewProjectModal, showDirectoryModal, menuOpenId, 
      showSortMenu, currentView]);

  const [uploadingFile, setUploadingFile] = useState(null); // 'projectPdf' | 'contractFile' | null

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const isGoogleUser = user?.authProvider === 'google';
    
    if (isGoogleUser) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        alert('Файл занадто великий. Максимум 50MB');
        return;
      }
    } else {
      const limitCheck = checkProjectFileLimit(file);
      if (!limitCheck.ok) {
        alert(limitCheck.error);
        return;
      }
    }
    
    const projectName = newProjectData.name.trim() || 'Без назви';
    const subfolder = fieldName === 'projectPdf' ? 'Проєкт' : 'Договір';
    
    setUploadingFile(fieldName);
    try {
      if (isGoogleUser) {
        const driveFile = await uploadFile(file, projectName, subfolder);
        setNewProjectData(prev => ({
          ...prev,
          [fieldName]: {
            driveFileId: driveFile.id,
            name: driveFile.name,
            webViewLink: driveFile.webViewLink,
            size: driveFile.size,
            type: driveFile.mimeType,
            storage: 'google'
          }
        }));
      } else {
        const supaFile = await uploadFileToSupabase(file, user.id, `projects/${fieldName}`);
        setNewProjectData(prev => ({
          ...prev,
          [fieldName]: {
            driveFileId: null,
            name: file.name,
            webViewLink: supaFile.url,
            storagePath: supaFile.path,
            size: file.size,
            type: file.type,
            storage: 'supabase'
          }
        }));
      }
    } catch (err) {
      console.error('Помилка завантаження:', err);
      alert(isGoogleUser 
        ? 'Не вдалося завантажити файл на Google Drive. Спробуйте перелогінитись.'
        : `Не вдалося завантажити файл. ${err?.message || 'Спробуйте ще раз.'}`
      );
    } finally {
      setUploadingFile(null);
      e.target.value = '';
    }
  };

  const handleFileDelete = async (fieldName) => {
    const fileData = newProjectData[fieldName];
    if (!fileData) {
      setNewProjectData(prev => ({ ...prev, [fieldName]: null }));
      return;
    }
    
    try {
      if (fileData.storage === 'supabase' && fileData.storagePath) {
        await deleteFileFromSupabase(fileData.storagePath);
      } else if (fileData.driveFileId) {
        await deleteFile(fileData.driveFileId);
      }
    } catch (err) {
      console.error('Помилка видалення:', err);
    }
    setNewProjectData(prev => ({ ...prev, [fieldName]: null }));
  };

  const handleOpenFile = (fileData) => {
    if (!fileData) return;
    const ext = fileData.name?.split('.').pop()?.toLowerCase() || '';
    const isPreviewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
    
    if (isPreviewable) {
      setPreviewFile({
        name: fileData.name || 'Файл',
        webViewLink: fileData.webViewLink || null,
        data: fileData.data || null,
        driveFileId: fileData.driveFileId || null,
        storage: fileData.storage || null,
      });
    } else if (fileData.webViewLink) {
      window.open(fileData.webViewLink, '_blank');
    }
  };

  const handleDownloadFile = async (fileData) => {
    if (!fileData) return;
    
    // Supabase Storage — просто відкриваємо URL
    if (fileData.storage === 'supabase' && fileData.webViewLink) {
      window.open(fileData.webViewLink, '_blank');
      return;
    }
    
    // Google Drive
    if (!fileData.driveFileId) return;
    try {
      const url = await getDownloadUrl(fileData.driveFileId);
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileData.name;
        link.target = '_blank';
        link.click();
      }
    } catch (err) {
      console.error('Помилка скачування:', err);
      if (fileData.webViewLink) {
        window.open(fileData.webViewLink, '_blank');
      }
    }
  };

  const handleCreateProject = (openProject = false) => {
    const projectData = {
      ...newProjectData,
      area: newProjectData.area ? parseFloat(newProjectData.area) : null
    };
    
    // Застосовуємо шаблони до попереднього кошторису
    let templateItems = [];
    if (newProjectData.selectedTemplateIds && newProjectData.selectedTemplateIds.length > 0) {
      newProjectData.selectedTemplateIds.forEach(templateId => {
        const template = templates.find(t => t.id === templateId);
        if (template && template.sections) {
          template.sections.forEach(section => {
            const newSection = {
              id: Date.now() + Math.random(),
              type: 'section',
              name: section.name,
              collapsed: false,
              includeATS: true,
              items: section.items.map(item => ({
                id: Date.now() + Math.random(),
                type: item.type || 'Робота',
                position: item.position,
                unit: item.unit,
                qty: 0,
                workPrice: item.clientPrice / 1.2, // Автоматична націнка 20%
                markup: 20,
                clientPrice: item.clientPrice,
                hidden: false
              }))
            };
            templateItems.push(newSection);
          });
          
          // Збільшуємо лічильник використань
          setTemplates(prev => prev.map(t => 
            t.id === templateId ? { ...t, usedCount: (t.usedCount || 0) + 1 } : t
          ));
        }
      });
    }
    
    // Передаємо шаблонні items в projectData
    projectData.templateItems = templateItems;
    
    const newProject = onCreateProject(projectData);
    resetNewProjectForm();
    setShowNewProjectModal(false);
    
    if (openProject && newProject) {
      onSelectProject(newProject.id);
    }
  };

  // Розрахунок статистики проєкту
  const getProjectStats = (project) => {
    let totalExpenses = 0;
    let totalReceived = 0;
    let workExpenses = 0;      // Тільки роботи
    let materialsExpenses = 0; // Тільки матеріали

    project.estimates.filter(e => !e.isPreliminary).forEach(estimate => {
      let estimateTotal = 0;
      let estimateWork = 0;
      let estimateMaterials = 0;
      let atsBase = 0;
      
      // Рахуємо витрати по секціях
      estimate.items?.forEach(section => {
        let sectionTotal = 0;
        let sectionWork = 0;
        let sectionMaterials = 0;
        
        section.items?.forEach(item => {
          if (!item.hidden) {
            const workCost = (item.qty || 0) * (item.workPrice || 0);
            const unitClientPrice = item.clientPrice !== undefined 
              ? item.clientPrice 
              : (item.workPrice || 0) * (1 + (item.markup || 0) / 100);
            const itemTotal = (item.qty || 0) * unitClientPrice;
            sectionTotal += itemTotal;
            
            // Розподіл по типах
            const itemType = item.type || 'Робота';
            if (itemType === 'Робота') {
              sectionWork += itemTotal;
            } else if (itemType === 'Матеріали') {
              sectionMaterials += itemTotal;
            }
          }
        });
        
        estimateTotal += sectionTotal;
        estimateWork += sectionWork;
        estimateMaterials += sectionMaterials;
        
        // АТС рахується від вартості секцій з includeATS
        if (section.includeATS !== false) {
          atsBase += sectionTotal;
        }
      });
      
      // Додаємо АТС
      if (estimate.atsEnabled) {
        const atsAmount = atsBase * (estimate.atsPercent || 15) / 100;
        estimateTotal += atsAmount;
        // АТС розподіляємо пропорційно (або можна додати тільки до робіт)
        const workRatio = estimateTotal > 0 ? estimateWork / (estimateWork + estimateMaterials || 1) : 0.5;
        estimateWork += atsAmount * workRatio;
        estimateMaterials += atsAmount * (1 - workRatio);
      }
      
      totalExpenses += estimateTotal;
      workExpenses += estimateWork;
      materialsExpenses += estimateMaterials;
      
      // Отримано
      estimate.payments?.forEach(p => {
        totalReceived += p.amount || 0;
      });
    });

    const reportsCount = project.estimates.filter(e => !e.isPreliminary).length;
    const lastReport = project.estimates
      .filter(e => !e.isPreliminary)
      .sort((a, b) => (b.id || 0) - (a.id || 0))[0];

    return { 
      totalExpenses, 
      totalReceived, 
      balance: totalReceived - totalExpenses,
      reportsCount,
      lastReportName: lastReport?.name || '-',
      workExpenses,
      materialsExpenses
    };
  };

  // Запуск туру коли є проєкти і тур ще не пройдений
  React.useEffect(() => {
    if (tourStep === -1 && projects.length > 0) {
      const timer = setTimeout(() => setTourStep(1), 500);
      return () => clearTimeout(timer);
    }
  }, [projects.length, tourStep]);

  // Фільтрація об'єктів
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.clientName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Фільтр по типу об'єкта
    if (objectTypeFilter !== 'all' && project.objectType !== objectTypeFilter) return false;
    
    if (filter === 'all') return true;
    if (filter === 'active') return project.status === 'active';
    if (filter === 'closed') return project.status === 'closed';
    if (filter === 'paused') return project.status === 'paused';
    return true;
  });

  // Сортування: pinned зверху, потім за вибраним критерієм
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    // Закріплені завжди зверху
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    
    const statsA = getProjectStats(a);
    const statsB = getProjectStats(b);
    
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'uk');
        break;
      case 'balance':
        comparison = statsA.balance - statsB.balance;
        break;
      case 'reports':
        comparison = statsA.reportsCount - statsB.reportsCount;
        break;
      case 'date':
      default:
        // За датою останнього оновлення - новіші зверху
        const dateA = new Date(a.updatedAt || a.startDate || 0);
        const dateB = new Date(b.updatedAt || b.startDate || 0);
        // Новіші (більша дата) мають бути першими
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  // Загальна статистика
  const totalStats = projects.reduce((acc, project) => {
    const stats = getProjectStats(project);
    return {
      totalProjects: acc.totalProjects + 1,
      activeProjects: acc.activeProjects + (project.status === 'active' ? 1 : 0),
      closedProjects: acc.closedProjects + (project.status === 'closed' ? 1 : 0),
      pausedProjects: acc.pausedProjects + (project.status === 'paused' ? 1 : 0),
      totalExpenses: acc.totalExpenses + stats.totalExpenses,
      totalReceived: acc.totalReceived + stats.totalReceived,
      totalBalance: acc.totalBalance + stats.balance
    };
  }, { totalProjects: 0, activeProjects: 0, closedProjects: 0, pausedProjects: 0, totalExpenses: 0, totalReceived: 0, totalBalance: 0 });

  const getStatusConfig = (status) => {
    switch(status) {
      case 'active':
        return { 
          label: 'Активний', 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          icon: <CheckCircle2 className="w-3.5 h-3.5" />,
          nextStatus: 'paused'
        };
      case 'closed':
        return { 
          label: 'Закритий', 
          bgColor: 'bg-gray-200', 
          textColor: 'text-gray-600',
          borderColor: 'border-gray-300',
          icon: <AlertCircle className="w-3.5 h-3.5" />,
          nextStatus: 'active'
        };
      case 'paused':
        return { 
          label: 'Призупинений', 
          bgColor: 'bg-amber-100', 
          textColor: 'text-amber-700',
          borderColor: 'border-amber-200',
          icon: <PauseCircle className="w-3.5 h-3.5" />,
          nextStatus: 'closed'
        };
      default:
        return { 
          label: 'Активний', 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          icon: <CheckCircle2 className="w-3.5 h-3.5" />,
          nextStatus: 'paused'
        };
    }
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('uk-UA', { maximumFractionDigits: 0 });
  };

  const getLatestReports = (project, count = 3) => {
    return project.estimates
      .filter(e => !e.isPreliminary)
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, count);
  };

  // Генерація PDF замовлення (A5)
  const generateOrderPDF = (card) => {
    const materialsHTML = (card.materials || []).map((m, idx) => `
      <tr>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;">${idx + 1}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;">${m.name}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;">${m.qty || 0}</td>
        <td style="padding:4px 8px;border:1px solid #ddd;text-align:center;">${m.unit || 'шт.'}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Замовлення — ${card.name}</title>
        <style>
          @page { size: A5 portrait; margin: 12mm; }
          body { font-family: Arial, sans-serif; font-size: 11px; color: #333; margin: 0; padding: 12mm; }
          h1 { font-size: 16px; color: #163248; margin: 0 0 4px; }
          h2 { font-size: 12px; color: #666; font-weight: normal; margin: 0 0 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { background: #163248; color: white; padding: 6px 8px; text-align: left; font-size: 10px; }
          td { font-size: 11px; }
          .footer { margin-top: 20px; font-size: 10px; color: #999; border-top: 1px solid #ddd; padding-top: 8px; }
          .date { font-size: 10px; color: #999; margin-bottom: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>📦 Замовлення матеріалів</h1>
        <h2>${card.name}</h2>
        ${card.contractor_name ? `<p style="font-size:11px;margin:0 0 4px;"><b>Підрядник:</b> ${card.contractor_name}</p>` : ''}
        <p class="date">Дата: ${new Date(card.created_at).toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        ${card.delivery?.needed ? `
          <div style="background:#fff8ed;border:1px solid #f59e0b;border-radius:6px;padding:8px 12px;margin-bottom:12px;">
            <p style="font-size:11px;margin:0 0 2px;font-weight:bold;color:#92400e;">🚚 Доставка</p>
            ${card.delivery.date ? `<p style="font-size:11px;margin:0 0 2px;color:#333;"><b>Дата:</b> ${new Date(card.delivery.date).toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })}</p>` : ''}
            ${card.delivery.address ? `<p style="font-size:11px;margin:0;color:#333;"><b>Адреса:</b> ${card.delivery.address}</p>` : ''}
          </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th style="width:30px;text-align:center;">№</th>
              <th>Матеріал</th>
              <th style="width:60px;text-align:center;">К-ть</th>
              <th style="width:50px;text-align:center;">Од.</th>
            </tr>
          </thead>
          <tbody>
            ${materialsHTML}
          </tbody>
        </table>
        
        <div class="footer">
          Всього позицій: ${(card.materials || []).length} • Сформовано в LIHTAR
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    setPreviewFile({
      name: `Замовлення — ${card.name}.pdf`,
      data: blobUrl,
      printHtml: htmlContent,
      isPrintPreview: true,
    });
  };

  // ── Об'єднання замовлень ──
  const mergeSelectedOrders = async (newName) => {
    if (selectedOrderIds.length < 2 || !user?.id) return;
    const selected = selectedOrderIds.map(id => (orderCards || []).find(c => c.id === id)).filter(Boolean);
    if (selected.length < 2) return;

    // Перевірка однакового статусу
    const statuses = [...new Set(selected.map(o => o.status || 'collecting'))];
    if (statuses.length > 1) {
      setConfirmModal({ show: true, title: 'Різні статуси', message: 'Об\'єднати можна тільки замовлення з однаковим статусом.', onConfirm: null, okText: 'Зрозуміло' });
      return;
    }

    // Дедуплікація матеріалів
    const normName = (n) => (n || '').normalize('NFC').toLowerCase().replace(/[^а-яіїєґa-z0-9]/gi, '');
    const mergedMaterials = [];
    selected.forEach(order => {
      (order.materials || []).forEach(m => {
        const mNorm = normName(m.name);
        const mOrigNorm = m.originalName ? normName(m.originalName) : null;
        const dupIdx = mergedMaterials.findIndex(existing => {
          if (m.productId && existing.productId === m.productId) return true;
          const exNorm = normName(existing.name);
          const exOrigNorm = existing.originalName ? normName(existing.originalName) : null;
          // Порівнюємо всі комбінації: name↔name, name↔originalName, originalName↔name
          if (exNorm === mNorm) return true;
          if (mOrigNorm && exNorm === mOrigNorm) return true;
          if (exOrigNorm && exOrigNorm === mNorm) return true;
          if (mOrigNorm && exOrigNorm && exOrigNorm === mOrigNorm) return true;
          return false;
        });
        if (dupIdx >= 0) {
          const ex = mergedMaterials[dupIdx];
          // Зберігаємо довшу (повнішу) назву
          const bestName = (m.name || '').length > (ex.name || '').length ? m.name : ex.name;
          // factQty мердж — тільки якщо сумісні одиниці і packSize
          if (ex.factQty && m.factQty && ex.factUnit === m.factUnit && ex.packSize === m.packSize) {
            const newFactQty = ex.factQty + m.factQty;
            mergedMaterials[dupIdx] = { ...ex, name: bestName, factQty: newFactQty, qty: Math.ceil(newFactQty / (ex.packSize || 1)), workPrice: ex.workPrice || m.workPrice, clientPrice: ex.clientPrice || m.clientPrice };
          } else {
            mergedMaterials[dupIdx] = { ...ex, name: bestName, qty: (ex.qty || 0) + (m.qty || 0), workPrice: ex.workPrice || m.workPrice, clientPrice: ex.clientPrice || m.clientPrice };
          }
        } else {
          mergedMaterials.push({ ...m, id: Date.now() + Math.random() });
        }
      });
    });

    // project_id — якщо однаковий, зберігаємо; якщо різний — null
    const projectIds = [...new Set(selected.map(o => o.project_id).filter(Boolean))];
    const projectId = projectIds.length === 1 ? projectIds[0] : null;
    const projectName = projectId ? selected.find(o => o.project_id === projectId)?.project_name : null;

    try {
      // Створюємо нове замовлення
      const { data: newOrder, error: insertError } = await supabase.from('order_cards').insert({
        user_id: user.id,
        name: newName || `Об'єднане (${selected.length})`,
        materials: mergedMaterials,
        status: statuses[0],
        project_id: projectId,
        project_name: projectName,
        delivery: null,
      }).select().single();
      if (insertError) throw insertError;

      // Видаляємо старі
      for (const id of selectedOrderIds) {
        await supabase.from('order_cards').delete().eq('id', id);
      }

      // Оновлюємо локальний стейт
      setOrderCards(prev => [newOrder, ...prev.filter(c => !selectedOrderIds.includes(c.id))]);
      setSelectedOrderIds([]);
      setShowMergeOrderModal(false);
      setMergeOrderName('');
      if (window.showToast) window.showToast(`Об'єднано ${selected.length} замовлень (${mergedMaterials.length} матеріалів)`);
    } catch (e) {
      console.error('mergeSelectedOrders error:', e);
      if (window.showToast) window.showToast('Помилка об\'єднання');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <style>{hideSpinnersStyle}</style>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
      <style>{`
        @media (max-width: 768px) {
          /* Title row with price mode */
          .mobile-title-row { display: flex !important; align-items: center !important; justify-content: space-between !important; }
          
          /* Filter bar - vertical stack */
          .mobile-filter-bar { 
            flex-direction: column !important; 
            align-items: stretch !important;
            gap: 10px !important; 
          }
          
          /* Search - full width */
          .mobile-search { width: 100% !important; }
          
          /* Status filters - horizontal scroll */
          .mobile-status-filters { 
            overflow-x: auto; 
            -webkit-overflow-scrolling: touch; 
            flex-shrink: 0;
            justify-content: flex-start !important;
          }
          
          /* Right controls - row */
          .mobile-controls { width: 100% !important; }
          
          /* Price mode switcher - show next to title on mobile */
          .mobile-price-mode { display: flex !important; }
          .desktop-price-mode { display: none !important; }
          
          /* FAB - center */
          .mobile-fab { 
            left: 50% !important; 
            transform: translateX(-50%) !important; 
            bottom: 20px !important;
            right: auto !important;
          }
          
          /* Main padding */
          .mobile-header-padding { padding-left: 16px !important; padding-right: 16px !important; }
          .mobile-main-padding { padding-left: 16px !important; padding-right: 16px !important; padding-bottom: 90px !important; }
        }
        @media (min-width: 769px) {
          .mobile-price-mode { display: none !important; }
        }
      `}</style>
      
      {/* Header - мінімалістична шапка */}
      <header className="border-b" style={{ borderColor: '#e5e7eb' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 mobile-header-padding">
          <div className="flex items-center justify-between">
            <h1 className="text-xl logo-text" style={{ letterSpacing: '0.05em' }}>LIHTAR</h1>
            
            <div className="flex items-center gap-2">
              
              {/* Кнопка До об'єктів — тільки на шаблонах */}
              {currentView === 'templates' && (
                <button 
                  onClick={() => setCurrentView('projects')}
                  className="group flex items-center justify-center h-9 rounded-full transition-all duration-300 overflow-hidden bg-white shadow-sm hover:shadow-md border border-gray-100"
                  style={{ minWidth: '36px' }}
                  title="До об'єктів"
                >
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                    <ChevronLeft className="w-4 h-4" style={{ color: '#163248' }} />
                  </div>
                  <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:pr-3 transition-all duration-300 whitespace-nowrap text-sm font-medium" style={{ color: '#163248' }}>
                    До об'єктів
                  </span>
                </button>
              )}
              
              {/* Меню користувача */}
              <div className="relative ml-1">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1 rounded-lg transition-colors hover:bg-gray-100"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#163248' }}>
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4" style={{ color: '#6b7280' }} />
              </button>
              
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div 
                    className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[260px]"
                    style={{ borderColor: '#e5e7eb' }}
                  >
                    {/* Профіль */}
                    <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#163248' }}>
                          <span className="text-white text-sm font-medium">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p className="text-sm font-medium truncate" style={{ color: '#163248' }}>{user?.name || 'Користувач'}</p>
                          <p className="text-xs truncate" style={{ color: '#9ca3af' }}>{user?.email || ''}</p>
                        </div>
                      </div>
                    </div>

                    {/* Тариф */}
                    <div className="px-4 py-3 border-b" style={{ borderColor: '#f3f4f6' }}>
                      {isPro ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Crown size={16} color="#f97316" />
                            <span className="text-sm font-semibold" style={{ color: '#163248' }}>PRO</span>
                          </div>
                          {isTrial && trialDaysLeft > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fff7ed', color: '#ea580c', fontWeight: 600 }}>
                              Тріал: {trialDaysLeft} {trialDaysLeft === 1 ? 'день' : trialDaysLeft < 5 ? 'дні' : 'днів'}
                            </span>
                          )}
                          {!isTrial && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: 600 }}>
                              Активна
                            </span>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium" style={{ color: '#6b7280' }}>FREE</span>
                          </div>
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              showPaywall('default');
                            }}
                            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                            style={{ backgroundColor: '#f97316', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            <Zap size={14} />
                            Перейти на PRO
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Посилання */}
                    <div className="py-1">
                      <a
                        href="https://t.me/lihtar_app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 flex items-center gap-2"
                        style={{ color: '#374151', textDecoration: 'none', display: 'flex' }}
                      >
                        <Mail size={14} color="#9ca3af" />
                        Підтримка (Telegram)
                      </a>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout();
                        }}
                        className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 flex items-center gap-2"
                        style={{ color: '#dc2626', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        Вийти
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-6 mobile-main-padding">
        {/* Заголовок сторінки */}
        <div className="mb-6 mobile-title-row">
          <h2 className="text-2xl font-semibold" style={{ color: '#163248' }}>
            {currentView === 'projects' ? 'Мої об\'єкти' : 'Шаблони'}
          </h2>
          {/* Price mode - mobile only */}
          {currentView === 'projects' && (
            <div 
              className="mobile-price-mode items-center border rounded-lg overflow-hidden"
              style={{ borderColor: '#e5e7eb' }}
            >
              {[
                { key: 'all', label: '₴/м²' },
                { key: 'work', label: 'Роботи' },
                { key: 'materials', label: 'Матеріали' }
              ].map(({ key, label }, idx) => (
                <button
                  key={key}
                  onClick={() => setPricePerSqmFilter(key)}
                  className="px-3 py-1.5 text-xs transition-colors"
                  style={{ 
                    backgroundColor: pricePerSqmFilter === key ? '#163248' : '#f9fafb',
                    color: pricePerSqmFilter === key ? '#ffffff' : '#6b7280',
                    borderLeft: idx > 0 ? '1px solid #e5e7eb' : 'none'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {currentView === 'templates' ? (
          /* ==================== СТОРІНКА ШАБЛОНІВ ==================== */
          <>
            {/* Перемикач груп + пошук */}
            {/* Mobile: 2 ряди (закладки → дії). Desktop: 1 ряд. */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto self-start sm:self-auto">
                {[
                  { key: 'templates', label: 'Шаблони', count: templates.length },
                  { key: 'contracts', label: 'Тендери', count: filledCards?.length || 0 },
                  { key: 'orders', label: 'Замовлення', count: orderCards?.length || 0 },
                ].map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setTemplateActiveGroup(key)}
                    className="px-3 py-1.5 text-sm rounded-md transition-all whitespace-nowrap"
                    style={{
                      backgroundColor: templateActiveGroup === key ? 'white' : 'transparent',
                      color: templateActiveGroup === key ? '#163248' : '#6b7280',
                      fontWeight: templateActiveGroup === key ? '600' : '400',
                      boxShadow: templateActiveGroup === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {label}
                    {count > 0 && <span className="ml-1 text-xs opacity-60">{count}</span>}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 justify-between sm:justify-end">
                <button
                  onClick={() => setShowCalculator(true)}
                  className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-all"
                  style={{ backgroundColor: '#163248', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Калькулятор
                </button>
                <button
                  onClick={() => { if (!isPro) { showPaywall('ai_import'); return; } setShowTemplateImportModal(true); }}
                  className="group flex items-center justify-center h-9 rounded-full transition-all duration-300 overflow-hidden bg-white shadow-sm hover:shadow-md border border-gray-100"
                  style={{ minWidth: '36px' }}
                  title="Імпорт позицій"
                >
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                    <Upload className="w-4 h-4" style={{ color: '#163248' }} />
                  </div>
                  <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:pr-3 transition-all duration-300 whitespace-nowrap text-sm font-medium" style={{ color: '#163248' }}>
                    Імпорт
                  </span>
                </button>
                <div className="relative flex-1 sm:flex-none sm:max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9ca3af' }} />
                  <input
                    type="text"
                    placeholder="Пошук..."
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg transition-colors"
                    style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb', color: '#163248' }}
                    onFocus={(e) => { e.target.style.borderColor = '#163248'; e.target.style.backgroundColor = '#ffffff'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.backgroundColor = '#f9fafb'; }}
                  />
                </div>
              </div>
            </div>

            {/* ===== ГРУПА: ШАБЛОНИ ===== */}
            {templateActiveGroup === 'templates' && (<>
              {/* ── LIHTAR шаблони (карусель) ── */}
              {sortedPublicTemplates.length > 0 && !templateSearchQuery && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#163248' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                      Шаблони LIHTAR
                      <span style={{ fontWeight: 400, color: '#9ca3af' }}>· {sortedPublicTemplates.length}</span>
                    </div>
                  </div>
                  {/* Теги категорій */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {['Всі', ...Array.from(new Set(sortedPublicTemplates.map(t => t.category).filter(Boolean)))].map(cat => (
                      <button key={cat} onClick={() => { setLihtarCategoryFilter(cat); const el = document.getElementById('lihtar-carousel'); if (el) el.scrollTo({ left: 0, behavior: 'smooth' }); }}
                        style={{ padding: '5px 14px', borderRadius: 20, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                          background: lihtarCategoryFilter === cat ? '#163248' : '#f1f5f9',
                          color: lihtarCategoryFilter === cat ? '#fff' : '#64748b' }}>
                        {cat}
                      </button>
                    ))}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <style>{`
                      #lihtar-carousel::-webkit-scrollbar{display:none}
                      .lihtar-template-card { flex: 0 0 calc((100% - 42px) / 4); min-width: 0; max-width: none; }
                      .lihtar-carousel-arrow { position: absolute; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; border-radius: 50%; background: #fff; border: 1px solid #e5e7eb; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 3; box-shadow: 0 2px 8px rgba(22,50,72,0.1); transition: background .15s, border-color .15s; padding: 0; }
                      .lihtar-carousel-arrow:hover { background: #163248; border-color: #163248; }
                      .lihtar-carousel-arrow:hover svg { color: #fff !important; }
                      .lihtar-arrow-left { left: -8px; }
                      .lihtar-arrow-right { right: -8px; }
                      @media (max-width: 1023px) { .lihtar-template-card { flex: 0 0 calc((100% - 14px) / 2); } }
                      @media (max-width: 639px) { .lihtar-template-card { flex: 0 0 100%; } .lihtar-carousel-arrow { display: none; } }
                    `}</style>
                    <button className="lihtar-carousel-arrow lihtar-arrow-left" aria-label="Попередні шаблони" onClick={() => { const el = document.getElementById('lihtar-carousel'); if (el) el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' }); }}>
                      <ChevronLeft size={18} style={{ color: '#163248' }} />
                    </button>
                    <button className="lihtar-carousel-arrow lihtar-arrow-right" aria-label="Наступні шаблони" onClick={() => { const el = document.getElementById('lihtar-carousel'); if (el) el.scrollBy({ left: el.clientWidth, behavior: 'smooth' }); }}>
                      <ChevronRight size={18} style={{ color: '#163248' }} />
                    </button>
                    <div id="lihtar-carousel" style={{ display: 'flex', gap: 14, overflowX: 'auto', scrollBehavior: 'smooth', paddingTop: 8, paddingBottom: 8, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {sortedPublicTemplates.filter(pt => lihtarCategoryFilter === 'Всі' || pt.category === lihtarCategoryFilter).map(pt => {
                      const items = pt.sections?.[0]?.items || [];
                      const isFav = favTemplates.includes(pt.id);
                      return (
                        <div key={pt.id}
                          className="lihtar-template-card"
                          style={{ background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 78%)', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'transform .18s ease, border-color .15s, box-shadow .15s' }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = '#163248'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(22,50,72,.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                          onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                          onClick={() => {
                            const viewTemplate = { id: 'pub_' + pt.id, name: pt.name, description: pt.description, sections: pt.sections, isPublic: true };
                            setEditingTemplate(viewTemplate);
                            setTemplateExpandedSections(pt.sections?.map(s => s.id) || []);
                            setTemplateViewOnly(true);
                            setShowTemplateModal(true);
                          }}
                        >
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #163248 0%, #163248 55%, #f97316 100%)' }} />
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <button onClick={(e) => { e.stopPropagation(); toggleFavTemplate(pt.id); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: isFav ? '#f97316' : '#163248', color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', padding: '3px 7px 3px 6px', borderRadius: 4, border: 'none', cursor: 'pointer', transition: 'background .2s' }}
                              title={isFav ? 'Прибрати з обраних' : 'Додати в обрані'}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="#fff" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                              LIHTAR
                            </button>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#163248', lineHeight: 1.3, marginBottom: 4, minHeight: '2.6em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{pt.name}</div>
                          <div style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.4, marginBottom: 12, minHeight: '2.8em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{pt.description || ''}</div>
                          {items.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '8px 10px', background: 'rgba(22,50,72,0.025)', borderRadius: 6, marginBottom: 10 }}>
                              {items.slice(0, 3).map((it, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6b7280', lineHeight: 1.35 }}>
                                  <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#94a3b8', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.position || it.name || ''}</span>
                                </div>
                              ))}
                              {items.length > 3 && (
                                <div style={{ fontSize: 10.5, color: '#9ca3af', paddingLeft: 9, fontWeight: 500 }}>+{items.length - 3} ще</div>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: '#6b7280', flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FolderOpen size={11} color="#6b7280" />{pt.sections?.length || 0} розд.</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FileText size={11} color="#6b7280" />{items.length} поз.</span>
                            {pt.use_count > 0 && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#9ca3af', marginLeft: 'auto' }}>
                                <TrendingUp size={10} color="#9ca3af" />{pt.use_count}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eef3f8' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 500 }}>{pt.category}</span>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Мої шаблони (заголовок) ── */}
              {sortedPublicTemplates.length > 0 && !templateSearchQuery && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#9ca3af', letterSpacing: '.5px', marginBottom: 12 }}>Мої шаблони</div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates
                  .filter(t => !templateSearchQuery ||
                    t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
                    t.description?.toLowerCase().includes(templateSearchQuery.toLowerCase())
                  )
                  .map((template, tIdx) => {
                    const locked = !isPro && tIdx >= 3;
                    if (locked) {
                      return (
                        <div key={template.id}
                          aria-disabled="true"
                          style={{ position: 'relative', borderRadius: 12, border: '1px solid rgba(249,115,22,0.3)', overflow: 'hidden', background: 'linear-gradient(135deg, #163248 0%, #1f4562 100%)', boxShadow: '0 2px 8px rgba(22,50,72,0.08)', transition: 'box-shadow 180ms ease' }}
                          onMouseOver={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(22,50,72,0.18)'; }}
                          onMouseOut={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,50,72,0.08)'; }}
                        >
                          <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.035 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`, mixBlendMode: 'overlay', pointerEvents: 'none', opacity: 0.6 }} />
                          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 45%, rgba(249,115,22,0.06) 100%)' }} />
                          <div style={{ position: 'absolute', inset: 0, padding: '16px 14px', filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none', userSelect: 'none' }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{template.name}</div>
                            {template.description && <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 12 }}>{template.description}</div>}
                            <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: '#cbd5e1' }}>
                              <span>{template.sections?.length || 0} розділів</span>
                              <span>{template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій</span>
                            </div>
                          </div>
                          <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '22px 22px 20px', gap: 10, minHeight: 240 }}>
                            <div style={{ color: '#ffffff', filter: 'drop-shadow(0 0 8px rgba(249,115,22,0.35))', marginTop: 2 }}>
                              <Crown size={28} color="#ffffff" />
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#ffffff', letterSpacing: -0.1 }}>Безлімітні шаблони</div>
                            <div style={{ fontSize: 12, color: '#8ba3b8', lineHeight: 1.5, maxWidth: 240 }}>Створюйте необмежену кількість власних шаблонів і заощаджуйте час на кожному кошторисі</div>
                            <button
                              onClick={() => showPaywall('maxTemplates')}
                              onMouseOver={e => { e.currentTarget.style.background = '#ea580c'; }}
                              onMouseOut={e => { e.currentTarget.style.background = '#f97316'; }}
                              aria-label="Оновити тариф до PRO для безлімітних шаблонів"
                              style={{ marginTop: 4, width: '100%', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, letterSpacing: 0.2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 140ms ease' }}
                            >
                              Оновити до PRO <ChevronRight size={14} color="#fff" />
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={template.id}
                        className="bg-white border transition-all cursor-pointer"
                        style={{ borderColor: '#e5e7eb', borderRadius: 12, padding: '16px 14px' }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = '#9ca3af'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                        onClick={() => {
                          setEditingTemplate(template);
                          setTemplateExpandedSections(template.sections?.map(s => s.id) || []);
                          setTemplateViewOnly(true);
                          setShowTemplateModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium line-clamp-2 break-words" style={{ color: '#163248' }}>{template.name}</h3>
                            {template.description && (
                              <p className="text-sm mt-1 line-clamp-2" style={{ color: '#6b7280' }}>{template.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                if (window.showToast) window.showToast('Публічні шаблони — скоро!');
                              }}
                              aria-label="Поділитись"
                              title="Поділитись (скоро)"
                              style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', opacity: 0.6, color: '#b0b5bc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 140ms ease, color 140ms ease, opacity 140ms ease' }}
                              onMouseOver={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.opacity = 1; }}
                              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#b0b5bc'; e.currentTarget.style.opacity = 0.6; }}
                            >
                              <Link2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingTemplate(template);
                                setTemplateExpandedSections(template.sections?.map(s => s.id) || []);
                                setTemplateViewOnly(false);
                                setShowTemplateModal(true);
                              }}
                              aria-label="Редагувати"
                              title="Редагувати"
                              style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', opacity: 0.6, color: '#b0b5bc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 140ms ease, color 140ms ease, opacity 140ms ease' }}
                              onMouseOver={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.opacity = 1; }}
                              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#b0b5bc'; e.currentTarget.style.opacity = 0.6; }}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmModal({
                                  show: true,
                                  title: 'Видалити шаблон?',
                                  message: `Ви впевнені, що хочете видалити шаблон "${template.name}"?`,
                                  onConfirm: () => setTemplates(prev => prev.filter(t => t.id !== template.id))
                                });
                              }}
                              aria-label="Видалити"
                              title="Видалити"
                              style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', opacity: 0.6, color: '#b0b5bc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 140ms ease, color 140ms ease, opacity 140ms ease' }}
                              onMouseOver={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.opacity = 1; }}
                              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#b0b5bc'; e.currentTarget.style.opacity = 0.6; }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <FolderOpen className="w-3.5 h-3.5" />
                            {template.sections?.length || 0} розділів
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" />
                            {template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
                          </span>
                          {template.usedCount > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              використано {template.usedCount}x
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {template.sections?.slice(0, 3).map((section, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-gray-50 rounded" style={{ padding: '6px 10px', fontSize: 12 }}>
                              <span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{section.name}</span>
                              <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 500, flexShrink: 0 }}>{section.items?.length || 0} поз.</span>
                            </div>
                          ))}
                          {(template.sections?.length || 0) > 3 && (
                            <div className="text-xs text-gray-400" style={{ padding: '2px 10px 0', fontWeight: 500 }}>
                              +{template.sections.length - 3} ще розділів
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
                {templates.filter(t => !templateSearchQuery || 
                  t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
                  t.description?.toLowerCase().includes(templateSearchQuery.toLowerCase())
                ).length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{templateSearchQuery ? 'Шаблонів не знайдено' : 'Шаблонів поки немає'}</p>
                    <p className="text-sm mt-1">
                      {templateSearchQuery ? 'Спробуйте змінити пошуковий запит' : 'Натисніть + щоб створити перший шаблон'}
                    </p>
                  </div>
                )}
              </div>
            </>)}

            {/* ===== ГРУПА: КАРТКИ ПІДРЯДУ ===== */}
            {templateActiveGroup === 'contracts' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(filledCards || [])
                  .filter(card => {
                    if (!templateSearchQuery) return true;
                    const q = templateSearchQuery.toLowerCase();
                    const tpl = card.contractor_data || card.template_snapshot;
                    return (tpl?.name || '').toLowerCase().includes(q) || 
                           (card.contractor_name || '').toLowerCase().includes(q);
                  })
                  .map(card => {
                    const tpl = card.contractor_data || card.template_snapshot;
                    const isPending = card.status === 'pending';
                    const isFilled = card.status === 'filled';
                    const isAccepted = card.status === 'accepted';
                    const hasResponses = (card.responsesCount || 0) > 0;
                    const isCardAccepted = card.status === 'accepted';
                    
                    const borderColor = isCardAccepted ? '#22c55e' : !hasResponses ? '#f59e0b' : '#3b82f6';
                    const statusLabel = isCardAccepted 
                      ? `✓ ${card.contractor_name || 'Обрано'}` 
                      : !hasResponses ? 'Очікує відповіді' 
                      : `${card.responsesCount} пропозиц${card.responsesCount === 1 ? 'ія' : card.responsesCount < 5 ? 'ії' : 'ій'}`;
                    const statusBg = isCardAccepted ? 'bg-green-100 text-green-700' : !hasResponses ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';

                    return (
                      <div
                        key={card.id}
                        className="bg-white border-2 rounded-lg p-5 transition-all hover:shadow-md cursor-pointer"
                        style={{ borderColor, opacity: isPending && card.responsesCount === 0 ? 0.8 : 1 }}
                        onClick={async () => {
                          setViewingContract(card);
                          setContractViewLoading(true);
                          setExpandedContractor(null);
                          try {
                            const { data } = await supabase
                              .from('contract_responses')
                              .select('*')
                              .eq('shared_template_id', card.id)
                              .order('created_at', { ascending: true });
                            setContractResponses(data || []);
                          } catch (e) {
                            setContractResponses([]);
                          }
                          setContractViewLoading(false);
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium line-clamp-2 break-words" style={{ color: '#163248' }}>
                              {tpl?.name || 'Без назви'}
                            </h3>
                            {tpl?.description && (
                              <p className="text-sm text-gray-500 mt-0.5 truncate">{tpl.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2" onClick={e => e.stopPropagation()}>
                            {/* Копіювати посилання — неактивне після accepted */}
                            {!isCardAccepted && (
                              <button
                                onClick={async () => {
                                  const link = `${window.location.origin}/template/${card.share_token}`;
                                  await navigator.clipboard.writeText(link);
                                  if (window.showToast) window.showToast('Посилання скопійовано!');
                                }}
                                className="p-1.5 rounded transition-colors hover:bg-blue-50"
                                title="Копіювати посилання"
                              >
                                <Link2 className="w-4 h-4" style={{ color: '#3b82f6' }} />
                              </button>
                            )}
                            {/* Видалити */}
                            <button
                              onClick={() => {
                                const linkedOrder = (orderCards || []).find(o => o.source_template_id === card.id);
                                const deleteTender = () => {
                                  setTimeout(() => {
                                    setConfirmModal({
                                      show: true,
                                      title: 'Видалити тендер?',
                                      message: `Видалити тендер "${tpl?.name || ''}"?`,
                                      onConfirm: async () => {
                                        await supabase.from('shared_templates').delete().eq('id', card.id);
                                        setFilledCards(prev => prev.filter(c => c.id !== card.id));
                                      }
                                    });
                                  }, 100);
                                };
                                
                                if (linkedOrder) {
                                  setConfirmModal({
                                    show: true,
                                    title: 'Замовлення матеріалів',
                                    message: `До тендера прив'язано замовлення "${linkedOrder.name}". Видалити його разом з тендером?`,
                                    confirmText: 'Видалити',
                                    cancelText: 'Залишити',
                                    onConfirm: async () => {
                                      await supabase.from('order_cards').delete().eq('id', linkedOrder.id);
                                      setOrderCards(prev => prev.filter(c => c.id !== linkedOrder.id));
                                      deleteTender();
                                    },
                                    onCancel: () => {
                                      deleteTender();
                                    }
                                  });
                                } else {
                                  setConfirmModal({
                                    show: true,
                                    title: 'Видалити тендер?',
                                    message: `Видалити тендер "${tpl?.name || ''}"?`,
                                    onConfirm: async () => {
                                      await supabase.from('shared_templates').delete().eq('id', card.id);
                                      setFilledCards(prev => prev.filter(c => c.id !== card.id));
                                    }
                                  });
                                }
                              }}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Видалити"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs mb-3">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${statusBg}`}>
                            {statusLabel}
                          </span>
                          <span className="text-gray-500">
                            {tpl?.sections?.length || 0} розділів • {tpl?.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          Створено {new Date(card.created_at).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}
                        </div>
                        {/* Зв'язок з карткою замовлення */}
                        {isCardAccepted && (() => {
                          const linkedOrder = (orderCards || []).find(o => o.source_template_id === card.id);
                          if (!linkedOrder) return null;
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTemplateActiveGroup('orders');
                              }}
                              className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-xs"
                              style={{ color: '#f59e0b' }}
                              title="Перейти до замовлення"
                            >
                              <Package className="w-3.5 h-3.5" />
                              <span className="font-medium">Замовлення: {linkedOrder.materials?.length || 0} матеріалів</span>
                            </button>
                          );
                        })()}
                      </div>
                    );
                  })
                }
                {(filledCards || []).filter(card => {
                  if (!templateSearchQuery) return true;
                  const q = templateSearchQuery.toLowerCase();
                  const tpl = card.contractor_data || card.template_snapshot;
                  return (tpl?.name || '').toLowerCase().includes(q) || (card.contractor_name || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{templateSearchQuery ? 'Карток не знайдено' : 'Тендерів поки немає'}</p>
                    <p className="text-sm mt-1">Натисніть + щоб створити тендер</p>
                  </div>
                )}
              </div>
            )}

            {/* ===== ГРУПА: КАРТКИ ЗАМОВЛЕННЯ ===== */}
            {templateActiveGroup === 'orders' && (<>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(orderCards || [])
                  .filter(card => {
                    if (!templateSearchQuery) return true;
                    const q = templateSearchQuery.toLowerCase();
                    return (card.name || '').toLowerCase().includes(q) || (card.contractor_name || '').toLowerCase().includes(q);
                  })
                  .map(card => {
                    const st = card.status || 'collecting';
                    const borderColor = { collecting: '#f59e0b', quote_received: '#3b82f6', added_to_estimate: '#22c55e' }[st] || '#f59e0b';
                    return (
                    <div
                      key={card.id}
                      onClick={() => {
                        if (selectedOrderIds.length > 0) {
                          setSelectedOrderIds(prev => prev.includes(card.id) ? prev.filter(id => id !== card.id) : [...prev, card.id]);
                        } else {
                          setEditingOrder(JSON.parse(JSON.stringify(card)));
                        }
                      }}
                      className={`bg-white border-2 rounded-lg p-5 transition-all hover:shadow-md cursor-pointer ${selectedOrderIds.includes(card.id) ? 'ring-2 ring-blue-300 bg-blue-50/30' : ''}`}
                      style={{ borderColor: selectedOrderIds.includes(card.id) ? '#3b82f6' : borderColor }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div
                            className="flex items-center justify-center flex-shrink-0 cursor-pointer"
                            style={{ width: 32, height: 32 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrderIds(prev => prev.includes(card.id) ? prev.filter(id => id !== card.id) : [...prev, card.id]);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.includes(card.id)}
                              readOnly
                              className="w-4 h-4 rounded pointer-events-none"
                              style={{ accentColor: '#163248' }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {(() => {
                              const st = card.status || 'collecting';
                              const cfg = { collecting: { label: 'Збирається', bg: '#fef3c7', color: '#d97706' }, quote_received: { label: 'КП отримана', bg: '#dbeafe', color: '#2563eb' }, added_to_estimate: { label: 'В кошторисі', bg: '#dcfce7', color: '#16a34a' } }[st] || { label: st, bg: '#f3f4f6', color: '#6b7280' };
                              return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
                            })()}
                            {card.project_name ? (
                              <span className="text-[10px] text-gray-500 truncate">• {card.project_name}</span>
                            ) : (
                              <span className="text-[10px] text-gray-400 italic">• Не прив'язано</span>
                            )}
                          </div>
                          <h3 className="font-medium line-clamp-2 break-words" style={{ color: '#163248' }}>{card.name}</h3>
                          {card.contractor_name && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <User className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
                              <span className="text-sm font-medium" style={{ color: '#f59e0b' }}>{card.contractor_name}</span>
                            </div>
                          )}
                        </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                          {/* Скачати PDF */}
                          <button
                            onClick={(e) => { e.stopPropagation(); generateOrderPDF(card); }}
                            className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Скачати PDF"
                          >
                            <Download className="w-4 h-4" style={{ color: '#3b82f6' }} />
                          </button>
                          {/* Видалити */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmModal({
                                show: true,
                                title: 'Видалити картку замовлення?',
                                message: `Видалити "${card.name}"?`,
                                onConfirm: async () => {
                                  await supabase.from('order_cards').delete().eq('id', card.id);
                                  setOrderCards(prev => prev.filter(c => c.id !== card.id));
                                }
                              });
                            }}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                            title="Видалити"
                          >
                            <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Матеріали */}
                      <div className="space-y-1">
                        {(card.materials || []).slice(0, 4).map((m, mIdx) => (
                          <div key={mIdx} className="flex justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                            <span className="text-gray-600 line-clamp-2 break-words mr-2">{m.name}</span>
                            <span className="text-gray-500 flex-shrink-0">{m.qty || 0} {m.unit || ""}</span>
                          </div>
                        ))}
                        {(card.materials || []).length > 4 && (
                          <div className="text-xs text-gray-400 px-2">+{card.materials.length - 4} інших...</div>
                        )}
                      </div>

                      <div className="text-xs text-gray-400 mt-3 flex items-center justify-between">
                        <span>
                          {new Date(card.created_at).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}
                          <span className="ml-2">{(card.materials || []).length} матеріалів</span>
                        </span>
                        {(() => {
                          const total = (card.materials || []).reduce((s, m) => s + ((m.clientPrice || m.workPrice || 0) * (m.qty || 0)), 0);
                          return total > 0 ? <span className="font-semibold" style={{ color: '#163248' }}>{Math.round(total).toLocaleString('uk-UA')} ₴</span> : null;
                        })()}
                      </div>
                      {/* Зв'язок з тендером */}
                      {card.source_template_id && (() => {
                        const linkedTender = (filledCards || []).find(t => t.id === card.source_template_id);
                        if (!linkedTender) return null;
                        return (
                          <button
                            onClick={() => setTemplateActiveGroup('contracts')}
                            className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-xs"
                            style={{ color: '#22c55e' }}
                            title="Перейти до тендеру"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span className="font-medium truncate">{linkedTender.template_snapshot?.name || 'Тендер'}</span>
                          </button>
                        );
                      })()}
                    </div>
                    );
                  })
                }
                {(orderCards || []).filter(card => {
                  if (!templateSearchQuery) return true;
                  const q = templateSearchQuery.toLowerCase();
                  return (card.name || '').toLowerCase().includes(q) || (card.contractor_name || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{templateSearchQuery ? 'Карток не знайдено' : 'Карток замовлення поки немає'}</p>
                    <p className="text-sm mt-1">Вони створюються автоматично при прийнятті пропозиції з матеріалами</p>
                  </div>
                )}
              </div>

              {/* Toolbar для об'єднання замовлень */}
              {selectedOrderIds.length >= 2 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white rounded-2xl shadow-lg border border-gray-200 px-4 py-3 flex items-center gap-3">
                  <span className="text-sm font-medium" style={{ color: '#163248' }}>Обрано: {selectedOrderIds.length}</span>
                  <div className="w-px h-5 bg-gray-200" />
                  <button
                    onClick={() => {
                      const selected = selectedOrderIds.map(id => (orderCards || []).find(c => c.id === id)).filter(Boolean);
                      const statuses = [...new Set(selected.map(o => o.status || 'collecting'))];
                      if (statuses.length > 1) {
                        setConfirmModal({ show: true, title: 'Різні статуси', message: 'Об\'єднати можна тільки замовлення з однаковим статусом.', onConfirm: null, okText: 'Зрозуміло' });
                        return;
                      }
                      // Перевіряємо чи є зв'язок з тендером
                      const withTender = selected.filter(o => o.source_template_id);
                      if (withTender.length > 0) {
                        const tenderNames = withTender.map(o => `"${o.name}"`).join(', ');
                        setConfirmModal({
                          show: true,
                          title: 'Зв\'язок з тендером',
                          message: `Замовлення ${tenderNames} прив'язані до тендера. При об'єднанні зв'язок буде розірвано. Продовжити?`,
                          confirmText: 'Об\'єднати',
                          confirmColor: '#163248',
                          onConfirm: () => { setShowMergeOrderModal(true); setMergeOrderName(selected[0]?.name || ''); },
                        });
                        return;
                      }
                      setShowMergeOrderModal(true);
                      setMergeOrderName(selected[0]?.name || '');
                    }}
                    className="px-4 py-2 text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#163248' }}
                  >
                    Об'єднати
                  </button>
                  <button
                    onClick={() => setSelectedOrderIds([])}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              )}

              {/* Модалка назви об'єднаного замовлення */}
              {showMergeOrderModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
                  <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Об'єднати замовлення</h3>
                      <button onClick={() => { setShowMergeOrderModal(false); setMergeOrderName(''); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-400" /></button>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                      {selectedOrderIds.length} замовлень буде об'єднано в одне. Оригінали видаляться.
                    </p>
                    <input
                      type="text"
                      value={mergeOrderName}
                      onChange={(e) => setMergeOrderName(e.target.value)}
                      placeholder="Назва нового замовлення"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter' && mergeOrderName.trim()) mergeSelectedOrders(mergeOrderName); }}
                    />
                    <div className="flex gap-3 mt-5">
                      <button onClick={() => mergeSelectedOrders(mergeOrderName)} className="flex-1 px-4 py-2.5 text-white rounded-xl font-medium hover:opacity-90 transition-opacity" style={{ backgroundColor: '#163248' }} disabled={!mergeOrderName.trim()}>Об'єднати</button>
                      <button onClick={() => { setShowMergeOrderModal(false); setMergeOrderName(''); }} className="px-4 py-2.5 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors" style={{ color: '#163248' }}>Скасувати</button>
                    </div>
                  </div>
                </div>
              )}
            </>)}

            {/* ===== ГРУПА: КАЛЬКУЛЯТОР ===== */}

          </>
        ) : (
          /* ==================== СТОРІНКА ОБ'ЄКТІВ ==================== */
          <>
        {/* Панель керування - єдиний рядок */}
        <div className="flex items-center justify-between gap-6 mb-6 pb-4 border-b mobile-filter-bar" style={{ borderColor: '#e5e7eb' }}>
          
          {/* Ліва частина: Пошук */}
          <div className="relative w-64 mobile-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Пошук об'єкта..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg transition-colors"
              style={{ 
                backgroundColor: '#f9fafb', 
                borderColor: '#e5e7eb',
                color: '#163248'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#163248';
                e.target.style.backgroundColor = '#ffffff';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = '#f9fafb';
              }}
            />
          </div>

          {/* Центр: Фільтри по статусу */}
          <div className="flex items-center gap-1 mobile-status-filters">
            {[
              { key: 'all', label: 'Всі', count: totalStats.totalProjects },
              { key: 'active', label: 'Активні', count: totalStats.activeProjects },
              { key: 'paused', label: 'Призупинені', count: totalStats.pausedProjects },
              { key: 'closed', label: 'Закриті', count: totalStats.closedProjects }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className="px-3 py-1.5 text-sm transition-all relative"
                style={{ 
                  color: filter === key ? '#163248' : '#6b7280',
                  fontWeight: filter === key ? '500' : '400'
                }}
              >
                {label}
                {count > 0 && (
                  <span className="ml-1" style={{ opacity: filter === key ? 0.7 : 0.5 }}>
                    {count}
                  </span>
                )}
                {/* Підкреслення активного стану */}
                {filter === key && (
                  <span 
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4/5 h-0.5 rounded-full"
                    style={{ backgroundColor: '#163248' }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Права частина: Селекти та сортування */}
          <div className="flex items-center gap-3 mobile-controls">
            {/* Тип об'єкта */}
            <select
              value={objectTypeFilter}
              onChange={(e) => setObjectTypeFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer transition-colors"
              style={{ 
                backgroundColor: '#f9fafb', 
                borderColor: '#e5e7eb',
                color: '#163248'
              }}
              onFocus={(e) => e.target.style.borderColor = '#163248'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="all">Всі типи</option>
              {OBJECT_TYPES.map(type => (
                <option key={type.key} value={type.key}>{type.label}</option>
              ))}
            </select>
            
            {/* Сортування */}
            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors"
                style={{ 
                  backgroundColor: '#f9fafb', 
                  borderColor: showSortMenu ? '#163248' : '#e5e7eb',
                  color: '#163248'
                }}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span>Сортувати</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                  <div 
                    className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[160px]"
                    style={{ borderColor: '#e5e7eb' }}
                  >
                    {[
                      { key: 'date', label: 'За датою' },
                      { key: 'name', label: 'За назвою' },
                      { key: 'balance', label: 'За балансом' },
                      { key: 'reports', label: 'За к-стю звітів' }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        className="w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between"
                        style={{ 
                          color: sortBy === key ? '#163248' : '#6b7280',
                          fontWeight: sortBy === key ? '500' : '400',
                          backgroundColor: sortBy === key ? '#f9fafb' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = sortBy === key ? '#f9fafb' : 'transparent'}
                        onClick={() => {
                          if (sortBy === key) {
                            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortBy(key);
                            setSortOrder('desc');
                          }
                          setShowSortMenu(false);
                        }}
                      >
                        {label}
                        {sortBy === key && (
                          <span style={{ color: '#9ca3af' }}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Перемикач ціни за м² */}
            <div 
              className="flex items-center border rounded-lg overflow-hidden desktop-price-mode"
              style={{ borderColor: '#e5e7eb' }}
            >
              {[
                { key: 'all', label: '₴/м²' },
                { key: 'work', label: 'Роботи' },
                { key: 'materials', label: 'Матеріали' }
              ].map(({ key, label }, idx) => (
                <button
                  key={key}
                  onClick={() => setPricePerSqmFilter(key)}
                  className="px-3 py-1.5 text-xs transition-colors"
                  style={{ 
                    backgroundColor: pricePerSqmFilter === key ? '#163248' : '#f9fafb',
                    color: pricePerSqmFilter === key ? '#ffffff' : '#6b7280',
                    borderLeft: idx > 0 ? '1px solid #e5e7eb' : 'none'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid об'єктів */}
        {sortedProjects.length === 0 && projects.length === 0 ? (
          /* Онбоард — перший вхід, немає об'єктів */
          <div className="bg-white rounded-lg border p-8 text-center max-w-md mx-auto" style={{ borderColor: '#e5e7eb' }}>
            <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#f0f4f8' }}>
              <FolderOpen className="w-7 h-7" style={{ color: '#163248' }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#163248' }}>
              Ласкаво просимо до LIHTAR
            </h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              Створіть перший об'єкт — квартиру, будинок або комерційне приміщення. Далі складіть попередній кошторис і контролюйте бюджет.
            </p>
            
            <div className="text-left space-y-3 mb-6 px-2">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: '#163248' }}>1</div>
                <div>
                  <div className="text-sm font-medium" style={{ color: '#374151' }}>Створіть об'єкт</div>
                  <div className="text-xs" style={{ color: '#9ca3af' }}>Вкажіть назву, тип і площу</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: '#163248' }}>2</div>
                <div>
                  <div className="text-sm font-medium" style={{ color: '#374151' }}>Складіть попередній кошторис</div>
                  <div className="text-xs" style={{ color: '#9ca3af' }}>Додайте позиції вручну або з шаблону</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: '#163248' }}>3</div>
                <div>
                  <div className="text-sm font-medium" style={{ color: '#374151' }}>Контролюйте бюджет</div>
                  <div className="text-xs" style={{ color: '#9ca3af' }}>Створюйте робочі звіти і слідкуйте за план/факт</div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setShowNewProjectModal(true);
              }}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#163248' }}
            >
              Створити перший об'єкт
            </button>
          </div>
        ) : sortedProjects.length === 0 ? (
          <div className="bg-white rounded-lg border p-16 text-center" style={{ borderColor: '#e5e7eb' }}>
            <FolderOpen className="w-12 h-12 mx-auto mb-4" style={{ color: '#d1d5db' }} />
            <h3 className="text-base font-medium mb-2" style={{ color: '#163248' }}>
              Об'єкти не знайдено
            </h3>
            <p style={{ color: '#6b7280' }} className="text-sm mb-2">
              Спробуйте змінити параметри пошуку або фільтру
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedProjects.map((project, sIdx) => {
              const stats = getProjectStats(project);
              const statusConfig = getStatusConfig(project.status);
              const isExpanded = expandedProjectId === project.id;
              const latestReports = getLatestReports(project);
              
              return (
                <div
                  key={project.id}
                  className={`relative bg-white rounded-lg border flex flex-col transition-all duration-200 ${!isExpanded ? 'hover:shadow-md hover:-translate-y-0.5' : ''}`}
                  data-tour={sIdx === 0 ? 'project-card' : undefined}
                  style={{ 
                    borderColor: project.pinned ? '#163248' : isExpanded ? '#163248' : '#e5e7eb',
                    borderWidth: project.pinned || isExpanded ? '1.5px' : '1px',
                    overflow: 'visible'
                  }}
                >
                  {/* Індикатор закріплення - мінімалістичний */}
                  {project.pinned && (
                    <div 
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#163248' }}
                    >
                      <Pin className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                  
                  {/* Заголовок картки */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-base line-clamp-2 break-words pr-2 flex items-center gap-2" style={{ color: '#163248' }}>
                          {project.name}
                          {(projectUnreadCounts[String(project.id)] || 0) > 0 && (
                            <span className="flex-shrink-0 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                              {projectUnreadCounts[String(project.id)] > 9 ? '9+' : projectUnreadCounts[String(project.id)]}
                            </span>
                          )}
                        </h3>
                        {project.address && (
                          <p className="text-sm truncate" style={{ color: '#6b7280' }}>{project.address}</p>
                        )}
                      </div>
                      
                      {/* Іконки дій */}
                      <div className="flex items-center gap-0.5">
                        {(() => {
                          const ss = projectShareStatuses[String(project.id)];
                          const isActive = ss && ss.is_active !== false;
                          const isLocked = ss?.is_locked;
                          const iconColor = !isActive ? '#9ca3af' : isLocked ? '#f59e0b' : '#16a34a';
                          const bgClass = !isActive ? '' : isLocked ? 'bg-amber-50' : 'bg-green-50';
                          return (
                        <button
                          className={`p-1.5 rounded-lg transition-colors hover:bg-gray-100 ${bgClass}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isPro) { showPaywall('client_link'); return; }
                            openShareModal(project);
                          }}
                          title="Посилання для замовника"
                        >
                          <Link2 className="w-4 h-4" style={{ color: iconColor }} />
                        </button>
                          );
                        })()}
                        <button
                          className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateProject(project.id, { pinned: !project.pinned });
                          }}
                          title={project.pinned ? 'Відкріпити' : 'Закріпити'}
                        >
                          {project.pinned ? (
                            <PinOff className="w-4 h-4" style={{ color: '#163248' }} />
                          ) : (
                            <Pin className="w-4 h-4" style={{ color: '#9ca3af' }} />
                          )}
                        </button>
                        <button
                          className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewProjectData({
                              name: project.name || '',
                              address: project.address || '',
                              objectType: project.objectType || '',
                              area: project.area?.toString() || '',
                              designerName: project.designerName || '',
                              designerPhone: project.designerPhone || '',
                              designerEmail: project.designerEmail || '',
                              clientName: project.clientName || '',
                              clientPhone: project.clientPhone || '',
                              clientEmail: project.clientEmail || '',
                              projectPdf: project.projectPdf || null,
                              contractFile: project.contractFile || null
                            });
                            setEditingProjectId(project.id);
                            setShowNewProjectModal(true);
                          }}
                          title="Редагувати"
                        >
                          <Pencil className="w-4 h-4" style={{ color: '#9ca3af' }} />
                        </button>
                        <button
                          className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              show: true,
                              title: 'Видалити об\'єкт?',
                              message: `Ви впевнені, що хочете видалити об'єкт "${project.name}"? Всі звіти та дані будуть втрачені.`,
                              onConfirm: () => onDeleteProject(project.id)
                            });
                          }}
                          title="Видалити"
                        >
                          <Trash2 className="w-4 h-4" style={{ color: '#9ca3af' }} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Статус - стриманий бейдж */}
                    <span 
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                      style={{ 
                        backgroundColor: project.status === 'active' ? '#f0fdf4' : project.status === 'paused' ? '#fffbeb' : '#f3f4f6',
                        color: project.status === 'active' ? '#166534' : project.status === 'paused' ? '#92400e' : '#4b5563'
                      }}
                    >
                      {statusConfig.label}
                    </span>
                  </div>
                  
                  {/* Контент картки - розгорнутий/згорнутий */}
                  <div 
                    className="px-4 pb-3 cursor-pointer flex-1"
                    onClick={() => setExpandedProjectId(isExpanded ? null : project.id)}
                  >
                    {isExpanded ? (
                      /* Розгорнутий вигляд */
                      <div className="space-y-3">
                        {/* Тип і площа */}
                        <div className="text-sm" style={{ color: '#6b7280' }}>
                          {[project.objectType, project.area ? `${project.area} м²` : null].filter(Boolean).join(' • ') || '—'}
                        </div>
                        
                        {/* Замовник */}
                        {project.clientName && (
                          <div className="text-sm" style={{ color: '#374151' }}>
                            Замовник: {project.clientName}
                          </div>
                        )}
                        
                        {/* Прогрес-бари */}
                        {(() => {
                          const preliminary = project.estimates?.find(e => e.isPreliminary);
                          if (!preliminary) return null;
                          
                          const allItems = (preliminary.items || []).flatMap(s => s.items || []);
                          const totalItems = allItems.length;
                          const completedItems = allItems.filter(i => i.completed).length;
                          const workPercent = totalItems > 0 ? Math.round(completedItems / totalItems * 100) : 0;
                          
                          let plannedBudget = 0;
                          (preliminary.items || []).forEach(section => {
                            (section.items || []).forEach(item => {
                              if (!item.hidden) {
                                const cp = item.clientPrice !== undefined 
                                  ? item.clientPrice 
                                  : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                                plannedBudget += Math.round((item.qty || 0) * cp * 100) / 100;
                              }
                            });
                          });
                          if (preliminary.atsEnabled ?? true) {
                            let atsBase = 0;
                            (preliminary.items || []).forEach(section => {
                              if (section.includeATS ?? true) {
                                (section.items || []).forEach(item => {
                                  if (!item.hidden) {
                                    const cp = item.clientPrice !== undefined 
                                      ? item.clientPrice 
                                      : Math.round((item.workPrice || 0) * (1 + (item.markup || 0) / 100) * 100) / 100;
                                    atsBase += Math.round((item.qty || 0) * cp * 100) / 100;
                                  }
                                });
                              }
                            });
                            plannedBudget += atsBase * (preliminary.atsPercent || 0) / 100;
                          }
                          
                          const budgetPercent = plannedBudget > 0 ? Math.round(stats.totalExpenses / plannedBudget * 100) : 0;
                          const budgetColor = budgetPercent > 100 ? '#dc2626' : budgetPercent > 80 ? '#f59e0b' : '#22c55e';
                          
                          return (totalItems > 0 || plannedBudget > 0) ? (
                            <div className="space-y-2.5 pt-1">
                              {totalItems > 0 && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[11px]" style={{ color: '#6b7280' }}>Роботи</span>
                                    <span className="text-[11px] font-medium" style={{ color: '#3b82f6' }}>{completedItems}/{totalItems} ({workPercent}%)</span>
                                  </div>
                                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(workPercent, 100)}%`, backgroundColor: '#3b82f6' }} />
                                  </div>
                                </div>
                              )}
                              {plannedBudget > 0 && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[11px]" style={{ color: '#6b7280' }}>Бюджет</span>
                                    <span className="text-[11px] font-medium" style={{ color: budgetColor }}>
                                      {formatCurrency(stats.totalExpenses)}/{formatCurrency(plannedBudget)} ({budgetPercent}%)
                                    </span>
                                  </div>
                                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(budgetPercent, 100)}%`, backgroundColor: budgetColor }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : null;
                        })()}
                        
                        {/* Кількість звітів */}
                        <div className="text-xs pt-1" style={{ color: '#9ca3af' }}>
                          {stats.reportsCount > 0 
                            ? `${stats.reportsCount} ${stats.reportsCount === 1 ? 'робочий звіт' : stats.reportsCount < 5 ? 'робочих звіти' : 'робочих звітів'}`
                            : 'Немає робочих звітів'
                          }
                        </div>
                      </div>
                    ) : (
                      /* Згорнутий вигляд - фінансова інформація */
                      <div className="space-y-2">
                        {/* Фінанси */}
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span style={{ color: '#6b7280' }}>Витрати:</span>
                            <span className="font-medium tabular-nums" style={{ color: '#163248' }}>{formatCurrency(stats.totalExpenses)} грн</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span style={{ color: '#6b7280' }}>Отримано:</span>
                            <span className="font-medium tabular-nums" style={{ color: '#163248' }}>{formatCurrency(stats.totalReceived)} грн</span>
                          </div>
                          
                          {/* Прогрес-бар - тонкий, спокійний */}
                          {stats.totalExpenses > 0 && (
                            <div className="pt-1">
                              <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                                <div 
                                  className="h-full rounded-full transition-all"
                                  style={{ 
                                    width: `${Math.min(100, (stats.totalReceived / stats.totalExpenses) * 100)}%`,
                                    backgroundColor: stats.balance >= 0 ? '#22c55e' : '#f59e0b'
                                  }}
                                />
                              </div>
                              <div className="flex justify-end text-[10px] mt-0.5" style={{ color: '#9ca3af' }}>
                                <span>{Math.round((stats.totalReceived / stats.totalExpenses) * 100)}%</span>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex justify-between text-sm pt-1">
                            <span style={{ color: '#6b7280' }}>Баланс:</span>
                            <span 
                              className="font-semibold tabular-nums"
                              style={{ color: stats.balance >= 0 ? '#16a34a' : '#dc2626' }}
                            >
                              {stats.balance >= 0 ? '+' : ''}{formatCurrency(stats.balance)} грн
                            </span>
                          </div>
                        </div>
                        
                        {/* Ціна за м² */}
                        {project.area && project.area > 0 && stats.totalExpenses > 0 && (
                          <div className="pt-2 border-t" style={{ borderColor: '#f3f4f6' }}>
                            <div className="flex justify-between items-center">
                              <span className="text-sm" style={{ color: '#6b7280' }}>
                                {pricePerSqmFilter === 'all' && 'Ціна за м²:'}
                                {pricePerSqmFilter === 'work' && 'Роботи за м²:'}
                                {pricePerSqmFilter === 'materials' && 'Матеріали за м²:'}
                              </span>
                              <span className="text-base font-semibold tabular-nums" style={{ color: '#163248' }}>
                                {formatCurrency(
                                  pricePerSqmFilter === 'all' 
                                    ? stats.totalExpenses / project.area
                                    : pricePerSqmFilter === 'work'
                                    ? stats.workExpenses / project.area
                                    : stats.materialsExpenses / project.area
                                )} ₴/м²
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Дата останнього редагування */}
                        <div className="pt-2 border-t flex items-center justify-end text-xs" style={{ borderColor: '#f3f4f6', color: '#9ca3af' }}>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {project.updatedAt ? new Date(project.updatedAt).toLocaleDateString('uk-UA') : '-'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Футер картки - плашка */}
                  <button
                    data-tour={sIdx === 0 ? 'editor-button' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isPro) {
                        const sorted = [...projects].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
                        if (sorted[0]?.id !== project.id) {
                          showPaywall('maxProjects');
                          return;
                        }
                      }
                      onSelectProject(project.id);
                    }}
                    className="w-full py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 rounded-b-lg"
                    style={{ backgroundColor: '#163248' }}
                  >
                    {!isPro && (() => { const sorted = [...projects].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)); return sorted[0]?.id !== project.id; })() && <Crown className="w-4 h-4 inline mr-1" />}
                    Відкрити редактор кошторисів
                  </button>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>

      {/* Плаваючі кнопки - лівий нижній кут, горизонтально (ховаються коли toolbar об'єднання видимий) */}
      {selectedOrderIds.length < 2 && (
      <div className="fixed bottom-6 left-6 flex items-center gap-3 z-40 mobile-fab">
        {/* Довідник */}
        <button
          onClick={() => setShowDirectoryModal(true)}
          className="w-14 h-14 bg-amber-500 hover:bg-amber-600 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center text-white"
          title="Довідник (Ctrl+K)"
          data-tour="fab-directory"
        >
          <Bookmark className="w-6 h-6" />
        </button>
        
        {/* Шаблони */}
        <button
          onClick={() => setCurrentView(currentView === 'templates' ? 'projects' : 'templates')}
          className={`w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center ${
            currentView === 'templates' 
              ? 'bg-blue-600 text-white' 
              : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
          }`}
          title="Шаблони"
          data-tour="fab-templates"
        >
          <Layers className="w-6 h-6" />
        </button>
        
        {/* Новий об'єкт / Новий шаблон / Новий тендер */}
        <button
          onClick={async () => {
            if (currentView === 'templates') {
              if (templateActiveGroup === 'contracts') {
                if (!isPro) { showPaywall('tenders'); return; }
                // Створити картку підряду — обрати шаблон або порожню
                setShowContractTemplateSelect(true);
              } else if (templateActiveGroup === 'orders') {
                // Відкрити модалку порожнього замовлення (без запису в базу)
                setEditingOrder({ id: null, name: 'Нове замовлення', materials: [] });
              } else {
                if (!isPro && templates.length >= 3) {
                  showPaywall('maxTemplates');
                } else {
                  setEditingTemplate({ id: null, name: '', description: '', sections: [] });
                  setTemplateExpandedSections([]);
                  setTemplateViewOnly(false);
                  setShowTemplateModal(true);
                }
              }
            } else {
              if (!isPro && projects.length >= 1) {
                showPaywall('maxProjects');
              } else {
                setShowNewProjectModal(true);
              }
            }
          }}
          className="w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center text-white"
          title={currentView === 'templates' 
            ? (templateActiveGroup === 'contracts' ? 'Новий тендер' : 'Новий шаблон (Ctrl+N)') 
            : 'Новий об\'єкт (Ctrl+N)'}
          data-tour="fab-add"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
      )}

      {/* Модалка перегляду/редагування замовлення */}
      {editingOrder && (
        <OrderViewModal
          key={editingOrder.id || 'new-order'}
          order={editingOrder}
          projects={projects}
          user={user}
          onClose={() => setEditingOrder(null)}
          onSave={(updated, isNew) => {
            if (isNew) {
              setOrderCards(prev => [updated, ...prev]);
            } else {
              setOrderCards(prev => prev.map(c => c.id === updated.id ? updated : c));
            }
            setEditingOrder(null);
          }}
          onDelete={async (orderId) => {
            try {
              await supabase.from('order_cards').delete().eq('id', orderId);
              setOrderCards(prev => prev.filter(c => c.id !== orderId));
              setEditingOrder(null);
              if (window.showToast) window.showToast('Замовлення видалено');
            } catch (e) {
              console.error(e);
              if (window.showToast) window.showToast('Помилка видалення');
            }
          }}
        />
      )}

      {/* Модалка перегляду картки підряду */}
      {viewingContract && (() => {
        const isAccepted = viewingContract.status === 'accepted' && viewingContract.contractor_data;
        const acceptedData = viewingContract.contractor_data;
        const acceptedName = viewingContract.contractor_name;
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4" onClick={(e) => { if (e.target === e.currentTarget) setViewingContract(null); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-2xl w-full max-h-[85vh] shadow-2xl flex flex-col" style={{ overflow: 'hidden' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b" style={{ flexShrink: 0 }}>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold truncate" style={{ color: '#163248' }}>
                  {viewingContract.template_snapshot?.name || 'Тендер'}
                </h3>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {isAccepted && acceptedName && (
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {acceptedName}
                    </span>
                  )}
                  <span>{viewingContract.template_snapshot?.sections?.length || 0} розділів</span>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-3">
                {!isAccepted && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const link = `${window.location.origin}/template/${viewingContract.share_token}`;
                      await navigator.clipboard.writeText(link);
                      if (window.showToast) window.showToast('Посилання скопійовано!');
                    }}
                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Копіювати посилання"
                  >
                    <Link2 className="w-5 h-5" style={{ color: '#3b82f6' }} />
                  </button>
                )}
                <button onClick={() => setViewingContract(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* Контент */}
            <div className="overflow-y-auto flex-1 modal-scroll">
              {isAccepted ? (
                /* ===== ПРИЙНЯТА КАРТКА — вигляд як шаблон ===== */
                <div className="p-5 space-y-4">
                  {(acceptedData?.sections || []).map((section, sIdx) => {
                    const sectionTotal = (section.items || []).reduce((sum, item) => sum + ((item.qty || 0) * (item.price || 0)), 0);
                    return (
                      <div key={section.id || sIdx}>
                        <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                          <span className="text-sm font-semibold" style={{ color: '#163248' }}>{sIdx + 1}. {section.name}</span>
                          <span className="text-sm font-semibold tabular-nums" style={{ color: '#163248' }}>
                            {sectionTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                          </span>
                        </div>
                        <div className="mt-1">
                          {(section.items || []).map((item, iIdx) => {
                            const itemTotal = (item.qty || 0) * (item.price || 0);
                            return (
                              <div key={item.id || iIdx} className="flex items-center py-2 border-b border-gray-100 text-sm">
                                <span className="flex-1 min-w-0 line-clamp-2 break-words" style={{ color: '#163248' }}>
                                  {item.position}
                                  {item.addedByContractor && (
                                    <span className="ml-1 text-[9px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded font-bold">+</span>
                                  )}
                                </span>
                                <span className="text-gray-500 mx-2 flex-shrink-0">{item.qty || 0} {item.unit}</span>
                                <span className="text-gray-500 mx-2 flex-shrink-0">× {(item.price || 0).toLocaleString('uk-UA')} ₴</span>
                                <span className="font-medium tabular-nums min-w-[70px] text-right flex-shrink-0" style={{ color: '#163248' }}>
                                  {itemTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {/* Загальна сума */}
                  {(() => {
                    const grandTotal = (acceptedData?.sections || []).reduce((sum, s) => 
                      sum + (s.items || []).reduce((sSum, item) => sSum + ((item.qty || 0) * (item.price || 0)), 0), 0);
                    return (
                      <>
                        <div className="flex justify-between items-center pt-3 border-t-2" style={{ borderColor: '#163248' }}>
                          <span className="font-bold" style={{ color: '#163248' }}>Разом:</span>
                          <span className="font-bold text-lg tabular-nums" style={{ color: '#163248' }}>
                            {grandTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                          </span>
                        </div>
                        {(acceptedData?.startDate || acceptedData?.endDate) && (
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 text-sm">
                            <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                            {acceptedData.startDate && (
                              <div><span className="text-gray-500">Початок: </span><span className="font-medium" style={{ color: '#163248' }}>{new Date(acceptedData.startDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
                            )}
                            {acceptedData.endDate && (
                              <div><span className="text-gray-500">Кінець: </span><span className="font-medium" style={{ color: '#163248' }}>{new Date(acceptedData.endDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : contractViewLoading ? (
                <div className="text-center py-12">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-xs text-gray-400">Завантаження...</p>
                </div>
              ) : contractResponses.length === 0 ? (
                <div className="px-4 sm:px-6 py-4">
                  {/* Бейдж статусу */}
                  <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-amber-700">Очікує відповідей від виконавців</span>
                  </div>
                  
                  {/* Вміст тендера */}
                  {(() => {
                    const snapshot = viewingContract.template_snapshot;
                    const sections = snapshot?.sections || [];
                    return sections.length > 0 ? (
                      <div className="space-y-3 mb-4">
                        {sections.map((section, sIdx) => {
                          const sectionTotal = (section.items || []).reduce((sum, item) => {
                            const price = item.clientPrice || item.price || 0;
                            return sum + (item.qty || 0) * price;
                          }, 0);
                          return (
                            <div key={sIdx} className="border rounded-xl overflow-hidden">
                              <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">{section.name}</span>
                                {sectionTotal > 0 && <span className="text-xs font-medium" style={{ color: '#163248' }}>{Math.round(sectionTotal).toLocaleString('uk-UA')} грн</span>}
                              </div>
                              {(section.items || []).map((item, iIdx) => (
                                <div key={iIdx} className="px-3 py-2 border-t text-sm flex items-center justify-between gap-2">
                                  <span className="text-gray-700 flex-1 min-w-0 line-clamp-2 break-words">{item.position || item.name}</span>
                                  <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-500">
                                    {item.qty > 0 && <span>{item.qty} {item.unit}</span>}
                                    {(item.clientPrice || item.price) > 0 && <span>{Math.round(item.clientPrice || item.price).toLocaleString('uk-UA')} грн</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 mb-4">
                        <Layers className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Тендер порожній</p>
                      </div>
                    );
                  })()}
                  
                  {/* Кнопки */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const snapshot = viewingContract.template_snapshot;
                        setEditingTemplate({
                          ...snapshot,
                          _isForContract: true,
                          _editingContractId: viewingContract.id
                        });
                        setTemplateViewOnly(false);
                        setShowTemplateModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90"
                      style={{ backgroundColor: '#163248' }}
                    >
                      <Pencil className="w-4 h-4" />
                      Редагувати
                    </button>
                    <button
                      onClick={async () => {
                        const link = `${window.location.origin}/template/${viewingContract.share_token}`;
                        await navigator.clipboard.writeText(link);
                        if (window.showToast) window.showToast('Посилання скопійовано!');
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border rounded-lg hover:bg-gray-50"
                    >
                      <Link2 className="w-4 h-4" />
                      Копіювати посилання
                    </button>
                  </div>
                </div>
              ) : (
                /* ===== АКОРДЕОН ПРОПОЗИЦІЙ ===== */
                <div className="divide-y">
                  {contractResponses.map((resp, rIdx) => {
                    const isExpanded = expandedContractor === resp.id;
                    const respData = resp.contractor_data;
                    const respTotal = (respData?.sections || []).reduce((sum, s) => 
                      sum + (s.items || []).reduce((sSum, item) => sSum + ((item.qty || 0) * (item.price || 0)), 0), 0
                    );

                    return (
                      <div key={resp.id}>
                        {/* Заголовок підрядника */}
                        <div 
                          className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedContractor(isExpanded ? null : resp.id)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {isExpanded 
                              ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            }
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][rIdx % 6] }}>
                              {(resp.contractor_name || '?')[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <span className="font-medium" style={{ color: '#163248' }}>{resp.contractor_name}</span>
                              <span className="text-xs text-gray-400 ml-2">
                                {new Date(resp.created_at).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}
                              </span>
                              {respData?.startDate && respData?.endDate && (
                                <span className="text-xs ml-2" style={{ color: '#8b5cf6' }}>
                                  {new Date(respData.startDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })} — {new Date(respData.endDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="font-bold tabular-nums" style={{ color: '#163248' }}>
                              {respTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                            </span>
                            {/* Кнопка Обрати */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModal({
                                  show: true,
                                  title: 'Обрати пропозицію?',
                                  message: `Обрати пропозицію від "${resp.contractor_name}" на ${respTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴ і закрити тендер?`,
                                  confirmText: 'Прийняти',
                                  confirmStyle: 'brand',
                                  onConfirm: async () => {
                                    try {
                                      // Відділяємо матеріали від робіт
                                      const respMaterials = resp.contractor_data?.materials || [];
                                      const workData = { ...resp.contractor_data };
                                      delete workData.materials;
                                      
                                      // Зберігаємо тільки роботи в картку тендеру
                                      const { error } = await supabase
                                        .from('shared_templates')
                                        .update({
                                          status: 'accepted',
                                          contractor_name: resp.contractor_name,
                                          contractor_data: workData,
                                          updated_at: new Date().toISOString()
                                        })
                                        .eq('id', viewingContract.id);
                                      
                                      if (error) throw error;
                                      
                                      // Створюємо картку замовлення з матеріалів
                                      if (respMaterials.length > 0) {
                                        const orderName = `${viewingContract.template_snapshot?.name || 'Тендер'} — ${resp.contractor_name}`;
                                        const { data: orderData } = await supabase
                                          .from('order_cards')
                                          .insert({
                                            user_id: user.id,
                                            name: orderName,
                                            materials: respMaterials,
                                            source_template_id: viewingContract.id,
                                            contractor_name: resp.contractor_name
                                          })
                                          .select()
                                          .single();
                                        
                                        if (orderData) {
                                          setOrderCards(prev => [orderData, ...prev]);
                                        }
                                      }
                                      
                                      // Оновлюємо локально
                                      const updated = { 
                                        ...viewingContract, 
                                        status: 'accepted', 
                                        contractor_name: resp.contractor_name, 
                                        contractor_data: workData 
                                      };
                                      setViewingContract(updated);
                                      setFilledCards(prev => prev.map(c => c.id === viewingContract.id ? { ...c, ...updated } : c));
                                      if (window.showToast) window.showToast(respMaterials.length > 0 ? 'Пропозицію обрано! Картку замовлення створено.' : 'Пропозицію обрано!');
                                    } catch (e) {
                                      console.error('Помилка:', e);
                                      if (window.showToast) window.showToast('Помилка збереження');
                                    }
                                  }
                                });
                              }}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-green-50 hover:bg-green-100 text-green-700"
                              title="Обрати цю пропозицію"
                            >
                              Обрати
                            </button>
                          </div>
                        </div>

                        {/* Розгорнуті дані */}
                        {isExpanded && (
                          <div className="bg-gray-50 px-5 pb-4">
                            {(respData?.sections || []).map((section, sIdx) => {
                              const sectionTotal = (section.items || []).reduce((sum, item) => sum + ((item.qty || 0) * (item.price || 0)), 0);
                              return (
                                <div key={section.id || sIdx} className="mb-3">
                                  <div className="flex justify-between items-center py-2">
                                    <span className="text-sm font-semibold" style={{ color: '#163248' }}>{sIdx + 1}. {section.name}</span>
                                    <span className="text-sm font-medium tabular-nums" style={{ color: '#163248' }}>
                                      {sectionTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                                    </span>
                                  </div>
                                  <div className="space-y-1">
                                    {(section.items || []).map((item, iIdx) => (
                                      <div key={item.id || iIdx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm">
                                        <div className="flex-1 min-w-0 mr-2">
                                          <span style={{ color: '#163248' }}>{item.position}</span>
                                          {item.addedByContractor && (
                                            <span className="ml-1 text-[9px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded font-bold">+</span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
                                          <span>{item.qty || 0} {item.unit}</span>
                                          <span>× {(item.price || 0).toLocaleString('uk-UA')} ₴</span>
                                          <span className="font-semibold tabular-nums min-w-[60px] text-right" style={{ color: '#163248' }}>
                                            {((item.qty || 0) * (item.price || 0)).toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                            
                            {/* Матеріали */}
                            {respData?.materials && respData.materials.length > 0 && (
                              <div className="mb-3 mt-4">
                                <div className="flex items-center gap-2 py-2">
                                  <span className="text-sm font-semibold" style={{ color: '#f59e0b' }}>Матеріали</span>
                                  <span className="text-xs text-gray-400">({respData.materials.length})</span>
                                </div>
                                <div className="space-y-1">
                                  {respData.materials.map((m, mIdx) => (
                                    <div key={m.id || mIdx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm">
                                      <span style={{ color: '#163248' }}>{m.name}</span>
                                      <span className="text-gray-500 flex-shrink-0">{m.qty || 0} {m.unit || ""}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Терміни */}
                            {(respData?.startDate || respData?.endDate) && (
                              <div className="mb-3 mt-2">
                                <div className="flex items-center gap-2 py-1">
                                  <span className="text-sm font-semibold" style={{ color: '#8b5cf6' }}>Терміни</span>
                                </div>
                                <div className="flex gap-4 bg-white rounded-lg px-3 py-2 text-sm">
                                  {respData.startDate && (
                                    <div><span className="text-gray-500">Початок: </span><span style={{ color: '#163248' }}>{new Date(respData.startDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}</span></div>
                                  )}
                                  {respData.endDate && (
                                    <div><span className="text-gray-500">Кінець: </span><span style={{ color: '#163248' }}>{new Date(respData.endDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })}</span></div>
                                  )}
                                  {respData.startDate && respData.endDate && (() => {
                                    const days = Math.ceil((new Date(respData.endDate) - new Date(respData.startDate)) / (1000 * 60 * 60 * 24));
                                    return days > 0 ? <span className="text-gray-400">({days} дн.)</span> : null;
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Модалка вибору шаблону для картки підряду */}
      {showContractTemplateSelect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4" onClick={(e) => { if (e.target === e.currentTarget) setShowContractTemplateSelect(false); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-md w-full max-h-[70vh] shadow-2xl flex flex-col" style={{ overflow: 'hidden' }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ flexShrink: 0 }}>
              <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Новий тендер</h3>
              <button onClick={() => setShowContractTemplateSelect(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2 modal-scroll">
              {/* Створити нову */}
              <button
                onClick={() => {
                  setShowContractTemplateSelect(false);
                  setEditingTemplate({ id: null, name: '', description: '', sections: [], _isForContract: true });
                  setTemplateExpandedSections([]);
                  setTemplateViewOnly(false);
                  setShowTemplateModal(true);
                }}
                className="w-full text-left p-4 border-2 border-dashed rounded-xl hover:bg-blue-50 transition-colors"
                style={{ borderColor: '#3b82f6' }}
              >
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5" style={{ color: '#3b82f6' }} />
                  <div className="font-medium" style={{ color: '#3b82f6' }}>Створити новий тендер</div>
                </div>
                <div className="text-xs text-gray-500 mt-1 ml-7">Порожня картка</div>
              </button>
              
              {/* Або обрати шаблон */}
              {templates.length > 0 && (
                <div className="text-xs text-gray-400 uppercase tracking-wide pt-2 pb-1 px-1">або на основі шаблону</div>
              )}
              {/* LIHTAR шаблони — кнопка */}
              {publicTemplates.length > 0 && (
                <>
                  <button
                    onClick={() => {
                      if (!isPro) { showPaywall('default'); return; }
                      setShowPublicInTender(prev => !prev);
                    }}
                    className="w-full text-left p-3 rounded-xl transition-all hover:shadow-sm"
                    style={{ border: '1.5px solid #f97316', background: 'linear-gradient(135deg, #fffbf5 0%, #fff 100%)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                        <span className="font-semibold text-sm" style={{ color: '#163248' }}>Шаблони LIHTAR</span>
                        {!isPro && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                      </div>
                      <ChevronDown size={14} style={{ color: '#9ca3af', transform: showPublicInTender ? 'rotate(180deg)' : 'none', transition: '.2s' }} />
                    </div>
                  </button>
                  {showPublicInTender && isPro && sortedPublicTemplates.map(pt => (
                    <button
                      key={'pub_' + pt.id}
                      onClick={() => {
                        setShowContractTemplateSelect(false);
                        setShowPublicInTender(false);
                        setEditingTemplate({
                          id: null,
                          name: pt.name,
                          description: pt.description || '',
                          sections: (pt.sections || []).map(s => ({
                            ...s,
                            id: Date.now() + Math.random(),
                            items: (s.items || []).map(item => ({
                              ...item,
                              id: Date.now() + Math.random(),
                              qty: 0,
                              price: 0,
                              clientPrice: 0
                            }))
                          })),
                          _isForContract: true
                        });
                        setTemplateExpandedSections(pt.sections?.map(s => s.id) || []);
                        setTemplateViewOnly(false);
                        setShowTemplateModal(true);
                      }}
                      className="w-full text-left p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      style={{ border: '1.5px solid #163248', background: '#fff' }}
                    >
                      <div className="font-medium text-sm" style={{ color: '#163248' }}>{pt.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {pt.sections?.length || 0} розділів • {pt.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
                      </div>
                    </button>
                  ))}
                </>
              )}

              {/* Мої шаблони */}
              {templates.length > 0 && (
                <div className="text-xs text-gray-400 uppercase tracking-wide pt-2 pb-1 px-1">{publicTemplates.length > 0 ? 'Мої шаблони' : 'або на основі шаблону'}</div>
              )}
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    setShowContractTemplateSelect(false);
                    setEditingTemplate({
                      id: null,
                      name: template.name,
                      description: template.description || '',
                      sections: (template.sections || []).map(s => ({
                        ...s,
                        id: Date.now() + Math.random(),
                        items: (s.items || []).map(item => ({
                          ...item,
                          id: Date.now() + Math.random(),
                          qty: 0,
                          price: 0,
                          clientPrice: 0
                        }))
                      })),
                      _isForContract: true
                    });
                    setTemplateExpandedSections(template.sections?.map(s => s.id) || []);
                    setTemplateViewOnly(false);
                    setShowTemplateModal(true);
                  }}
                  className="w-full text-left p-4 border rounded-xl hover:bg-gray-50 transition-colors"
                  style={{ borderColor: '#e5e7eb' }}
                >
                  <div className="font-medium" style={{ color: '#163248' }}>{template.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {template.sections?.length || 0} розділів • {template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Модалка створення/редагування об'єкта */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-2xl w-full max-h-[90vh] shadow-2xl flex flex-col" style={{ overflow: 'hidden' }}>
            {/* Header модалки */}
            <div className="flex items-center justify-between p-6 border-b" style={{ flexShrink: 0 }}>
              <h3 className="text-xl font-semibold text-gray-900">
                {editingProjectId ? 'Редагувати об\'єкт' : 'Новий об\'єкт'}
              </h3>
              <button
                onClick={() => {
                  setShowNewProjectModal(false);
                  setEditingProjectId(null);
                  resetNewProjectForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Контент модалки — scrollable */}
            <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
            <div className="p-6 space-y-6">
              {/* Основна інформація */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Основна інформація</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Назва об'єкта <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={newProjectData.name}
                      onChange={(e) => setNewProjectData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Наприклад: ЖК Казковий, кв. 42"
                      className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${
                        !newProjectData.name.trim() ? 'border-gray-300' : 'border-green-300'
                      }`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Адреса об'єкта</label>
                    <input
                      type="text"
                      value={newProjectData.address}
                      onChange={(e) => setNewProjectData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="м. Київ, вул. Хрещатик 1"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Тип об'єкта</label>
                    <select
                      value={newProjectData.objectType}
                      onChange={(e) => setNewProjectData(prev => ({ ...prev, objectType: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
                    >
                      <option value="">Оберіть тип</option>
                      {OBJECT_TYPES.map(type => (
                        <option key={type.key} value={type.key}>{type.icon} {type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Площа (м²) <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        value={newProjectData.area}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, area: e.target.value }))}
                        placeholder="0"
                        className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${
                          !newProjectData.area ? 'border-gray-300' : 'border-green-300'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Шаблон для попереднього кошторису - тільки при створенні */}
              {!editingProjectId && templates.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Шаблон для попереднього кошторису</h4>
                  <div className="space-y-2">
                    {templates.map(template => (
                      <label
                        key={template.id}
                        className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                          newProjectData.selectedTemplateIds?.includes(template.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={newProjectData.selectedTemplateIds?.includes(template.id) || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewProjectData(prev => ({
                                ...prev,
                                selectedTemplateIds: [...(prev.selectedTemplateIds || []), template.id]
                              }));
                            } else {
                              setNewProjectData(prev => ({
                                ...prev,
                                selectedTemplateIds: (prev.selectedTemplateIds || []).filter(id => id !== template.id)
                              }));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{template.name}</div>
                          <div className="text-xs text-gray-500">
                            {template.sections?.length || 0} розділів • {template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
                          </div>
                        </div>
                        <Layers className="w-5 h-5 text-gray-400" />
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Обрані шаблони будуть додані до Попереднього кошторису
                  </p>
                </div>
              )}
              
              {/* Дизайнер */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Дизайнер</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ім'я</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={newProjectData.designerName}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, designerName: e.target.value }))}
                        placeholder="Ім'я дизайнера"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Телефон</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={newProjectData.designerPhone}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, designerPhone: e.target.value }))}
                        placeholder="+380"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={newProjectData.designerEmail}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, designerEmail: e.target.value }))}
                        placeholder="email@example.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Замовник */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Замовник</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ім'я</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={newProjectData.clientName}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, clientName: e.target.value }))}
                        placeholder="Ім'я замовника"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Телефон</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={newProjectData.clientPhone}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, clientPhone: e.target.value }))}
                        placeholder="+380"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={newProjectData.clientEmail}
                        onChange={(e) => setNewProjectData(prev => ({ ...prev, clientEmail: e.target.value }))}
                        placeholder="email@example.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Документи */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Документи</h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Архітектурний/дизайн-проєкт */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Архітектурний або дизайн-проєкт</label>
                    {newProjectData.projectPdf ? (
                      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate flex-1">{newProjectData.projectPdf.name}</span>
                        <button
                          onClick={() => handleOpenFile(newProjectData.projectPdf)}
                          className="p-1 hover:bg-blue-100 rounded"
                          title="Переглянути"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDownloadFile(newProjectData.projectPdf)}
                          className="p-1 hover:bg-blue-100 rounded"
                          title="Завантажити"
                        >
                          <ArrowRight className="w-4 h-4 text-blue-600 rotate-90" />
                        </button>
                        <button
                          onClick={() => handleFileDelete('projectPdf')}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Видалити"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <label onClick={(e) => { if (!isPro) { e.preventDefault(); showPaywall('files'); } }} className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl transition-all ${
                        !isPro
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : uploadingFile === 'projectPdf' 
                            ? 'border-blue-400 bg-blue-50/30' 
                            : 'border-gray-300 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30'
                      }`}>
                        {!isPro ? (
                          <>
                            <Crown className="w-5 h-5 text-amber-500" />
                            <span className="text-sm text-gray-400">PRO функція</span>
                          </>
                        ) : uploadingFile === 'projectPdf' ? (
                          <>
                            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-blue-600">Завантаження...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-500">Завантажити PDF</span>
                            {user?.authProvider !== 'google' && (
                              <span className="text-[10px] text-amber-600">до 10 МБ</span>
                            )}
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'projectPdf')}
                              className="hidden"
                            />
                          </>
                        )}
                      </label>
                    )}
                  </div>
                  
                  {/* Договір */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Договір</label>
                    {newProjectData.contractFile ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                        <File className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate flex-1">{newProjectData.contractFile.name}</span>
                        <button
                          onClick={() => handleOpenFile(newProjectData.contractFile)}
                          className="p-1 hover:bg-green-100 rounded"
                          title="Переглянути"
                        >
                          <Eye className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={() => handleDownloadFile(newProjectData.contractFile)}
                          className="p-1 hover:bg-green-100 rounded"
                          title="Завантажити"
                        >
                          <ArrowRight className="w-4 h-4 text-green-600 rotate-90" />
                        </button>
                        <button
                          onClick={() => handleFileDelete('contractFile')}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Видалити"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <label onClick={(e) => { if (!isPro) { e.preventDefault(); showPaywall('files'); } }} className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl transition-all ${
                        !isPro
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : uploadingFile === 'contractFile' 
                            ? 'border-green-400 bg-green-50/30' 
                            : 'border-gray-300 cursor-pointer hover:border-green-400 hover:bg-green-50/30'
                      }`}>
                        {!isPro ? (
                          <>
                            <Crown className="w-5 h-5 text-amber-500" />
                            <span className="text-sm text-gray-400">PRO функція</span>
                          </>
                        ) : uploadingFile === 'contractFile' ? (
                          <>
                            <div className="w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-green-600">Завантаження...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-500">Завантажити договір</span>
                            {user?.authProvider !== 'google' && (
                              <span className="text-[10px] text-amber-600">до 10 МБ</span>
                            )}
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => handleFileUpload(e, 'contractFile')}
                              className="hidden"
                            />
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
            
            {/* Footer модалки */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-6 border-t bg-gray-50 gap-3" style={{ flexShrink: 0 }}>
              {/* Підказка — ховаємо на мобільних */}
              <span className="text-xs text-gray-400 hidden sm:block">
                * Назва та площа обов'язкові
              </span>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowNewProjectModal(false);
                    setEditingProjectId(null);
                    resetNewProjectForm();
                  }}
                  className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-full transition-all"
                >
                  Скасувати
                </button>
                
                {editingProjectId ? (
                  /* Кнопка для редагування */
                  <button
                    onClick={() => {
                      if (!newProjectData.name.trim()) {
                        alert('Введіть назву об\'єкта');
                        return;
                      }
                      if (!newProjectData.area) {
                        alert('Введіть площу об\'єкта');
                        return;
                      }
                      const projectData = {
                        name: newProjectData.name,
                        address: newProjectData.address,
                        objectType: newProjectData.objectType,
                        area: newProjectData.area ? parseFloat(newProjectData.area) : null,
                        designerName: newProjectData.designerName,
                        designerPhone: newProjectData.designerPhone,
                        designerEmail: newProjectData.designerEmail,
                        clientName: newProjectData.clientName,
                        clientPhone: newProjectData.clientPhone,
                        clientEmail: newProjectData.clientEmail,
                        projectPdf: newProjectData.projectPdf,
                        contractFile: newProjectData.contractFile,
                        updatedAt: new Date().toISOString()
                      };
                      onUpdateProject(editingProjectId, projectData);
                      setShowNewProjectModal(false);
                      setEditingProjectId(null);
                      resetNewProjectForm();
                    }}
                    disabled={!newProjectData.name.trim() || !newProjectData.area}
                    className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all ${
                      newProjectData.name.trim() && newProjectData.area
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Зберегти
                  </button>
                ) : (
                  /* Кнопки для створення */
                  <>
                    <button
                      onClick={() => {
                        if (!newProjectData.name.trim()) {
                          alert('Введіть назву об\'єкта');
                          return;
                        }
                        if (!newProjectData.area) {
                          alert('Введіть площу об\'єкта');
                          return;
                        }
                        handleCreateProject(false);
                      }}
                      disabled={!newProjectData.name.trim() || !newProjectData.area}
                      className={`flex-1 sm:flex-none px-4 sm:px-5 py-2.5 text-sm font-medium rounded-full transition-all ${
                        newProjectData.name.trim() && newProjectData.area
                          ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-200'
                      }`}
                    >
                      Зберегти
                    </button>
                    <button
                      onClick={() => {
                        if (!newProjectData.name.trim()) {
                          alert('Введіть назву об\'єкта');
                          return;
                        }
                        if (!newProjectData.area) {
                          alert('Введіть площу об\'єкта');
                          return;
                        }
                        handleCreateProject(true);
                      }}
                      disabled={!newProjectData.name.trim() || !newProjectData.area}
                      className={`flex-1 sm:flex-none px-4 sm:px-5 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                        newProjectData.name.trim() && newProjectData.area
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Зберегти і відкрити
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модалка перегляду PDF */}
      {/* Універсальна модалка перегляду файлів */}
      {previewFile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { if (previewFile.data?.startsWith('blob:')) URL.revokeObjectURL(previewFile.data); setPreviewFile(null); }}}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-4xl w-full h-[92vh] sm:h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="font-medium text-gray-900 truncate pr-2">{previewFile.name}</h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Друк */}
                {previewFile.isPrintPreview && (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (printWindow && previewFile.printHtml) {
                        printWindow.document.write(previewFile.printHtml);
                        printWindow.document.close();
                        printWindow.focus();
                        setTimeout(() => printWindow.print(), 250);
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-medium hover:opacity-90"
                    style={{ backgroundColor: '#163248' }}
                    title="Друкувати / Зберегти PDF"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Друк</span>
                  </button>
                )}
                {previewFile.webViewLink && (
                  <button
                    onClick={() => window.open(previewFile.webViewLink, '_blank')}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title="Відкрити в новій вкладці"
                  >
                    <Eye className="w-5 h-5 text-gray-500" />
                  </button>
                )}
                {previewFile.driveFileId && !previewFile.isPrintPreview && (
                  <button
                    onClick={() => handleDownloadFile(previewFile)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title="Завантажити"
                  >
                    <Download className="w-5 h-5 text-gray-500" />
                  </button>
                )}
                <button
                  onClick={() => { if (previewFile.data?.startsWith('blob:')) URL.revokeObjectURL(previewFile.data); setPreviewFile(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-2 sm:p-4 overflow-hidden bg-gray-50">
              {(() => {
                const ext = previewFile.name?.split('.').pop()?.toLowerCase() || '';
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
                const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
                
                if (isImage) {
                  const directSrc = previewFile.data || (previewFile.storage === 'supabase' ? previewFile.webViewLink : null);
                  if (directSrc) {
                    return (
                      <div className="w-full h-full flex items-center justify-center overflow-auto">
                        <img src={directSrc} alt={previewFile.name} className="max-w-full max-h-full object-contain rounded-lg" />
                      </div>
                    );
                  }
                  if (previewFile.driveFileId) {
                    const ImgLoader = () => {
                      const [blobSrc, setBlobSrc] = React.useState(null);
                      const [loading, setLoading] = React.useState(true);
                      React.useEffect(() => {
                        getFileBlobUrl(previewFile.driveFileId).then(url => {
                          setBlobSrc(url);
                          setLoading(false);
                        }).catch(() => setLoading(false));
                      }, []);
                      if (loading) return <div className="flex items-center gap-2 text-gray-400"><div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>Завантаження...</div>;
                      if (!blobSrc) return <p className="text-gray-400 text-sm">Не вдалось завантажити зображення</p>;
                      return <img src={blobSrc} alt={previewFile.name} className="max-w-full max-h-full object-contain rounded-lg" />;
                    };
                    return (
                      <div className="w-full h-full flex items-center justify-center overflow-auto">
                        <ImgLoader />
                      </div>
                    );
                  }
                }
                
                if (isOffice) {
                  const icons = { doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', ppt: '📑', pptx: '📑' };
                  const labels = { doc: 'Word', docx: 'Word', xls: 'Excel', xlsx: 'Excel', ppt: 'PowerPoint', pptx: 'PowerPoint' };
                  return (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                      <span className="text-6xl">{icons[ext] || '📄'}</span>
                      <p className="text-lg font-medium text-gray-700">{previewFile.name}</p>
                      <p className="text-sm text-gray-400">{labels[ext] || 'Office'} документ</p>
                      <div className="flex gap-3 mt-2">
                        {previewFile.driveFileId && (
                          <button
                            onClick={() => handleDownloadFile(previewFile)}
                            className="px-5 py-2.5 text-white rounded-xl text-sm font-medium hover:opacity-90"
                            style={{ backgroundColor: '#163248' }}
                          >
                            Завантажити
                          </button>
                        )}
                        {previewFile.webViewLink && (
                          <button
                            onClick={() => window.open(previewFile.webViewLink, '_blank')}
                            className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
                          >
                            Відкрити в новій вкладці
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
                
                const src = previewFile.data || previewFile.webViewLink;
                if (src) {
                  const iframeSrc = previewFile.data 
                    ? previewFile.data 
                    : (previewFile.webViewLink?.includes('drive.google.com') 
                        ? previewFile.webViewLink.replace('/view', '/preview') 
                        : previewFile.webViewLink);
                  return (
                    <iframe
                      src={iframeSrc}
                      className="w-full h-full rounded-lg border bg-white"
                      title="Перегляд файлу"
                      allow="autoplay"
                    />
                  );
                }
                
                return (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-3">
                    <FileText className="w-12 h-12 text-gray-300" />
                    <p className="text-sm">Файл недоступний для перегляду</p>
                    {previewFile.webViewLink && (
                      <button
                        onClick={() => window.open(previewFile.webViewLink, '_blank')}
                        className="px-4 py-2 text-white rounded-lg text-sm hover:opacity-90"
                        style={{ backgroundColor: '#163248' }}
                      >
                        Відкрити в новій вкладці
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка створення/редагування шаблону */}
      {/* Модалка імпорту на сторінці шаблонів */}
      <ImportModal
        isOpen={showTemplateImportModal}
        onClose={() => setShowTemplateImportModal(false)}
        templates={templates}
        setTemplates={setTemplates}
        setFilledCards={setFilledCards}
        user={user}
        setConfirmModal={setConfirmModal}
        mode="templates"
      />

      <MaterialCalculator
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
        user={user}
        onCreateOrder={(order) => setOrderCards(prev => [order, ...prev])}
      />

      {showTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${editingTemplate.isPublic ? 'bg-gradient-to-br from-blue-50 to-orange-50' : editingTemplate._isForContract ? 'bg-amber-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`} style={editingTemplate.isPublic ? { border: '1.5px solid #c8dde6' } : {}}>
                  {editingTemplate.isPublic
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    : editingTemplate._isForContract
                    ? <FileText className="w-5 h-5 text-amber-600" />
                    : <Layers className="w-5 h-5 text-blue-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  {templateViewOnly ? (
                    <>
                      <div className="text-lg font-semibold" style={{ color: '#163248', wordBreak: 'break-word' }}>{editingTemplate.name}</div>
                      {editingTemplate.description && <div className="text-xs text-gray-500 mt-0.5" style={{ wordBreak: 'break-word' }}>{editingTemplate.description}</div>}
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={editingTemplate.name}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                        className="text-lg font-semibold bg-transparent border-none focus:outline-none focus:bg-gray-100 focus:px-2 rounded w-full"
                        style={{ color: '#163248' }}
                        placeholder={editingTemplate._isForContract ? "Назва тендеру" : "Назва шаблону"}
                      />
                      <input
                        type="text"
                        value={editingTemplate.description || ''}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, description: e.target.value }))}
                        className="block text-xs text-gray-500 bg-transparent border-none focus:outline-none focus:bg-gray-100 focus:px-2 rounded w-full"
                        placeholder="Опис (необов'язково)"
                      />
                    </>
                  )}
                </div>
              </div>
              <button 
                onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); setTemplateViewOnly(false); }} 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Toolbar — hide in view mode */}
            {!templateViewOnly && (
            <div className="p-4 border-b flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Пошук позицій..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {/* Кнопка "Використати шаблон" — тільки для тендеру */}
              {editingTemplate._isForContract && templates.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowTemplateSelectForTender(prev => !prev)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-sm font-medium transition-colors"
                  >
                    <Layers className="w-4 h-4" /> Із шаблону
                  </button>
                  {showTemplateSelectForTender && (
                    <div className="absolute right-0 top-full mt-1 bg-white border rounded-xl shadow-lg z-10 w-64 max-h-48 overflow-y-auto">
                      {templates.map(tpl => (
                        <button key={tpl.id} onClick={() => {
                          setEditingTemplate(prev => ({
                            ...prev,
                            name: prev.name || tpl.name,
                            description: prev.description || tpl.description,
                            sections: (tpl.sections || []).map(s => ({
                              ...s,
                              id: Date.now() + Math.random(),
                              items: (s.items || []).map(item => ({
                                ...item,
                                id: Date.now() + Math.random(),
                                qty: 0,
                                price: 0,
                                clientPrice: 0
                              }))
                            }))
                          }));
                          setTemplateExpandedSections(tpl.sections?.map(s => s.id) || []);
                          setShowTemplateSelectForTender(false);
                        }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium" style={{ color: '#163248' }}>{tpl.name}</span>
                          <span className="text-xs text-gray-400 ml-2">{tpl.sections?.length || 0} розд.</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => {
                  setNewTemplateSectionName('');
                  setShowTemplateAddSection(true);
                }}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#163248' }}
              >
                <Plus className="w-4 h-4" /> Розділ
              </button>
            </div>
            )}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {(!editingTemplate.sections || editingTemplate.sections.length === 0) ? (
                <div className="text-center py-12">
                  <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">Шаблон порожній</p>
                  <p className="text-sm text-gray-400">Створіть розділ та додайте позиції</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {editingTemplate.sections.map((section, sIdx) => {
                    const isExpanded = templateExpandedSections.includes(section.id);
                    
                    return (
                      <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Section header */}
                        <div 
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => setTemplateExpandedSections(prev => 
                            prev.includes(section.id) 
                              ? prev.filter(id => id !== section.id) 
                              : [...prev, section.id]
                          )}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <span className="font-medium flex-1 min-w-0" style={{ color: '#163248' }}>
                              <input
                                type="text"
                                value={section.name}
                                onChange={(e) => {
                                  const newSections = [...editingTemplate.sections];
                                  newSections[sIdx].name = e.target.value;
                                  setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                                }}
                                className="bg-transparent border-none focus:outline-none focus:bg-white focus:px-2 focus:rounded w-full font-medium"
                                style={{ color: '#163248' }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">({section.items?.length || 0})</span>
                          </div>
                          {!templateViewOnly && <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                setAddItemToTemplateSectionIdx(sIdx);
                                setNewTemplateItem({ position: '', unit: 'м²', price: '', type: 'Робота' });
                                setShowTemplateAddItem(true);
                                if (!templateExpandedSections.includes(section.id)) {
                                  setTemplateExpandedSections(prev => [...prev, section.id]);
                                }
                              }}
                              className="p-1.5 hover:bg-green-100 rounded-lg"
                              title="Додати позицію"
                            >
                              <Plus className="w-3.5 h-3.5 text-green-600" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmModal({
                                  show: true,
                                  title: 'Видалити розділ?',
                                  message: `Ви впевнені, що хочете видалити розділ "${section.name}"?`,
                                  onConfirm: () => setEditingTemplate(prev => ({
                                    ...prev,
                                    sections: prev.sections.filter((_, i) => i !== sIdx)
                                  }))
                                });
                              }}
                              className="p-1.5 hover:bg-red-100 rounded-lg"
                              title="Видалити розділ"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>}
                        </div>
                        
                        {/* Section items - показуємо тільки якщо розгорнуто */}
                        {isExpanded && (
                          <div className="divide-y divide-gray-100">
                            {(!section.items || section.items.length === 0) ? (
                              <div className="px-4 py-6 text-center">
                                <p className="text-sm text-gray-400 mb-2">Розділ порожній</p>
                                <button
                                  onClick={() => {
                                    setAddItemToTemplateSectionIdx(sIdx);
                                    setNewTemplateItem({ position: '', unit: 'м²', price: '', type: 'Робота' });
                                    setShowTemplateAddItem(true);
                                  }}
                                className="text-sm text-blue-600 hover:text-blue-700"
                              >
                                + Додати позицію
                              </button>
                            </div>
                          ) : (
                            section.items.map((item, iIdx) => {
                              // Колір кружечка по типу
                              const typeColors = {
                                'Робота': 'bg-green-500',
                                'Матеріали': 'bg-yellow-500',
                                'Комплектація': 'bg-blue-500',
                                'Транспорт': 'bg-orange-500',
                                'Інші послуги': 'bg-purple-500'
                              };
                              const typeLabels = {
                                'Робота': 'Р',
                                'Матеріали': 'М',
                                'Комплектація': 'К',
                                'Транспорт': 'Т',
                                'Інші послуги': 'І'
                              };
                              
                              return (
                                <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 group">
                                  {/* Кружечок типу */}
                                  <div
                                    className={`w-6 h-6 ${typeColors[item.type] || 'bg-gray-400'} text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0`}
                                    title={item.type || 'Робота'}
                                  >
                                    {typeLabels[item.type] || 'Р'}
                                  </div>
                                  
                                  {/* Назва позиції */}
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium line-clamp-2 break-words block" style={{ color: '#163248' }}>{item.position || 'Без назви'}</span>
                                  </div>
                                  
                                  {/* Одиниця */}
                                  {editingTemplate._isForContract ? (
                                    <select
                                      value={item.unit || 'шт.'}
                                      onChange={(e) => {
                                        const newSections = JSON.parse(JSON.stringify(editingTemplate.sections));
                                        newSections[sIdx].items[iIdx].unit = e.target.value;
                                        setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                                      }}
                                      className="text-xs text-gray-500 w-14 text-center border rounded px-1 py-0.5 focus:outline-none"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                  ) : (
                                    <span className="text-xs text-gray-500 w-12 text-center">{item.unit}</span>
                                  )}
                                  
                                  {/* Ціна або Кількість */}
                                  {editingTemplate._isForContract ? (
                                    <input
                                      type="number"
                                      value={item.qty || ''}
                                      onChange={(e) => {
                                        const newSections = JSON.parse(JSON.stringify(editingTemplate.sections));
                                        newSections[sIdx].items[iIdx].qty = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                                        setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                                      }}
                                      placeholder="0"
                                      className="w-20 text-sm text-right px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 tabular-nums"
                                      style={{ borderColor: '#e5e7eb', color: '#163248' }}
                                      inputMode="decimal"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <span className="text-sm font-medium w-20 text-right" style={{ color: '#163248' }}>
                                      {(item.clientPrice || 0).toLocaleString('uk-UA')} ₴
                                    </span>
                                  )}
                                  
                                  {/* Кнопки редагування/видалення */}
                                  {!templateViewOnly && <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => {
                                        setEditTemplateItem({
                                          sectionIdx: sIdx,
                                          itemIdx: iIdx,
                                          position: item.position,
                                          unit: item.unit || 'м²',
                                          price: item.clientPrice?.toString() || '',
                                          qty: item.qty?.toString() || '',
                                          type: item.type || 'Робота'
                                        });
                                        setShowTemplateEditItem(true);
                                      }}
                                      className="p-1.5 hover:bg-gray-200 rounded"
                                      title="Редагувати"
                                    >
                                      <Pencil className="w-3.5 h-3.5 text-gray-400" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        const newSections = [...editingTemplate.sections];
                                        newSections[sIdx].items = newSections[sIdx].items.filter((_, i) => i !== iIdx);
                                        setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                                      }}
                                      className="p-1.5 hover:bg-red-100 rounded"
                                      title="Видалити"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                                    </button>
                                  </div>}
                                </div>
                              );
                            })
                          )}
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between rounded-b-2xl">
              <div className="text-sm text-gray-600">
                {editingTemplate.sections?.length || 0} розділів • {editingTemplate.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} позицій
              </div>
              {templateViewOnly ? (
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); setTemplateViewOnly(false); }} 
                    className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium" 
                    style={{ color: '#163248' }}
                  >
                    Закрити
                  </button>
                  {!editingTemplate.isPublic && (!isPro ? templates.indexOf(editingTemplate) < 3 : true) && (
                    <button
                      onClick={() => setTemplateViewOnly(false)}
                      className="px-5 py-2 text-white rounded-xl text-sm font-medium transition-opacity hover:opacity-90 flex items-center gap-2"
                      style={{ backgroundColor: '#163248' }}
                    >
                      <Pencil className="w-4 h-4" /> Редагувати
                    </button>
                  )}
                </div>
              ) : (
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); setTemplateViewOnly(false); }} 
                  className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium" 
                  style={{ color: '#163248' }}
                >
                  Скасувати
                </button>
                <button
                  onClick={async () => {
                    if (!editingTemplate.name.trim()) {
                      alert('Введіть назву шаблону');
                      return;
                    }
                    
                    // Якщо редагуємо існуючий тендер
                    if (editingTemplate._editingContractId) {
                      try {
                        const tpl = { ...editingTemplate };
                        const contractId = tpl._editingContractId;
                        delete tpl._editingContractId;
                        delete tpl._isForContract;
                        
                        const { error } = await supabase
                          .from('shared_templates')
                          .update({ template_snapshot: tpl })
                          .eq('id', contractId);
                        
                        if (error) throw error;
                        
                        // Оновлюємо локальний стейт
                        setFilledCards(prev => prev.map(c => 
                          c.id === contractId ? { ...c, template_snapshot: tpl } : c
                        ));
                        if (viewingContract && viewingContract.id === contractId) {
                          setViewingContract(prev => ({ ...prev, template_snapshot: tpl }));
                        }
                        if (window.showToast) window.showToast('Тендер оновлено!');
                      } catch (e) {
                        console.error('Помилка оновлення тендера:', e);
                        if (window.showToast) window.showToast('Помилка оновлення');
                      }
                    // Якщо створюємо новий тендер
                    } else if (editingTemplate._isForContract) {
                      try {
                        const tpl = { ...editingTemplate };
                        delete tpl._isForContract;
                        tpl.id = Date.now();
                        
                        const { data, error } = await supabase
                          .from('shared_templates')
                          .insert({
                            user_id: user.id,
                            template_id: String(tpl.id),
                            template_snapshot: tpl,
                            status: 'pending'
                          })
                          .select()
                          .single();
                        
                        if (error) throw error;
                        
                        const link = `${window.location.origin}/template/${data.share_token}`;
                        await navigator.clipboard.writeText(link);
                        setFilledCards(prev => [{ ...data, responsesCount: 0 }, ...prev]);
                        if (window.showToast) window.showToast('Тендер створено, посилання скопійовано!');
                      } catch (e) {
                        console.error('Помилка:', e);
                        if (window.showToast) window.showToast('Помилка створення картки');
                      }
                    } else if (editingTemplate.id) {
                      setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? editingTemplate : t));
                    } else {
                      if (!isPro && templates.length >= 3) {
                        showPaywall('maxTemplates');
                      } else {
                        const newTemplate = { ...editingTemplate, id: Date.now(), createdAt: new Date().toISOString(), usedCount: 0 };
                        setTemplates(prev => [...prev, newTemplate]);
                        trackEvent('template_create', { name: newTemplate.name });
                      }
                    }
                    
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                    setTemplateViewOnly(false);
                  }}
                  className="px-5 py-2 text-white rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#163248' }}
                >
                  {editingTemplate._editingContractId ? 'Зберегти зміни' : editingTemplate._isForContract ? 'Створити тендер' : editingTemplate.id ? 'Зберегти' : 'Створити'}
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка додавання розділу в шаблон */}
      {showTemplateAddSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Новий розділ</h3>
            <input
              type="text"
              value={newTemplateSectionName}
              onChange={(e) => setNewTemplateSectionName(e.target.value)}
              placeholder="Назва розділу..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTemplateSectionName.trim()) {
                  const newSection = { id: Date.now(), name: newTemplateSectionName.trim(), items: [] };
                  setEditingTemplate(prev => ({ ...prev, sections: [...(prev.sections || []), newSection] }));
                  setTemplateExpandedSections(prev => [...prev, newSection.id]);
                  setNewTemplateSectionName('');
                  setShowTemplateAddSection(false);
                }
              }}
            />
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowTemplateAddSection(false); setNewTemplateSectionName(''); }} 
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" 
                style={{ color: '#163248' }}
              >
                Скасувати
              </button>
              <button 
                onClick={() => {
                  if (newTemplateSectionName.trim()) {
                    const newSection = { id: Date.now(), name: newTemplateSectionName.trim(), items: [] };
                    setEditingTemplate(prev => ({ ...prev, sections: [...(prev.sections || []), newSection] }));
                    setTemplateExpandedSections(prev => [...prev, newSection.id]);
                    setNewTemplateSectionName('');
                    setShowTemplateAddSection(false);
                  }
                }} 
                disabled={!newTemplateSectionName.trim()} 
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-opacity ${newTemplateSectionName.trim() ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-400'}`}
                style={newTemplateSectionName.trim() ? { backgroundColor: '#163248' } : {}}
              >
                Створити
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка додавання позиції в шаблон */}
      {showTemplateAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Нова позиція</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Назва</label>
                <input 
                  type="text" 
                  value={newTemplateItem.position} 
                  onChange={(e) => { let val = e.target.value; if (val.length > 0) val = val[0].toUpperCase() + val.slice(1); setNewTemplateItem(prev => ({ ...prev, position: val })); }} 
                  placeholder="Назва позиції..." 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                  autoFocus 
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тип</label>
                  <select 
                    value={newTemplateItem.type} 
                    onChange={(e) => setNewTemplateItem(prev => ({ ...prev, type: e.target.value }))} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    {['Робота', 'Матеріали', 'Комплектація', 'Транспорт', 'Інші послуги'].map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Одиниця</label>
                  <select 
                    value={newTemplateItem.unit} 
                    onChange={(e) => setNewTemplateItem(prev => ({ ...prev, unit: e.target.value }))} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </div>
              </div>
              {editingTemplate._isForContract ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Кількість</label>
                  <input 
                    type="number" 
                    value={newTemplateItem.qty || ''} 
                    onChange={(e) => setNewTemplateItem(prev => ({ ...prev, qty: e.target.value }))} 
                    placeholder="0" 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                    inputMode="decimal"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ціна (з націнкою)</label>
                  <input 
                    type="number" 
                    value={newTemplateItem.price} 
                    onChange={(e) => setNewTemplateItem(prev => ({ ...prev, price: e.target.value }))} 
                    placeholder="0" 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => { setShowTemplateAddItem(false); setAddItemToTemplateSectionIdx(null); setNewTemplateItem({ position: '', unit: 'м²', price: '', type: 'Робота' }); }} 
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" 
                style={{ color: '#163248' }}
              >
                Скасувати
              </button>
              <button 
                onClick={() => {
                  if (newTemplateItem.position.trim() && addItemToTemplateSectionIdx !== null) {
                    const newItem = { 
                      id: Date.now(), 
                      position: newTemplateItem.position.trim(), 
                      unit: newTemplateItem.unit, 
                      clientPrice: editingTemplate._isForContract ? 0 : (parseFloat(newTemplateItem.price) || 0),
                      qty: editingTemplate._isForContract ? (parseFloat(newTemplateItem.qty) || 0) : 0,
                      type: newTemplateItem.type 
                    };
                    const newSections = [...editingTemplate.sections];
                    newSections[addItemToTemplateSectionIdx].items = [...(newSections[addItemToTemplateSectionIdx].items || []), newItem];
                    setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                    setShowTemplateAddItem(false);
                    setAddItemToTemplateSectionIdx(null);
                    setNewTemplateItem({ position: '', unit: 'м²', price: '', type: 'Робота' });
                  }
                }} 
                disabled={!newTemplateItem.position.trim()} 
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-opacity ${newTemplateItem.position.trim() ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-400'}`}
                style={newTemplateItem.position.trim() ? { backgroundColor: '#163248' } : {}}
              >
                Додати
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка редагування позиції в шаблоні */}
      {showTemplateEditItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#163248' }}>Редагувати позицію</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Назва</label>
                <input 
                  type="text" 
                  value={editTemplateItem.position} 
                  onChange={(e) => { let val = e.target.value; if (val.length > 0) val = val[0].toUpperCase() + val.slice(1); setEditTemplateItem(prev => ({ ...prev, position: val })); }} 
                  placeholder="Назва позиції..." 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                  autoFocus 
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тип</label>
                  <select 
                    value={editTemplateItem.type} 
                    onChange={(e) => setEditTemplateItem(prev => ({ ...prev, type: e.target.value }))} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    {['Робота', 'Матеріали', 'Комплектація', 'Транспорт', 'Інші послуги'].map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Одиниця</label>
                  <select 
                    value={editTemplateItem.unit} 
                    onChange={(e) => setEditTemplateItem(prev => ({ ...prev, unit: e.target.value }))} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    {UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </div>
              </div>
              {editingTemplate?._isForContract ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Кількість</label>
                  <input 
                    type="number" 
                    value={editTemplateItem.qty || ''} 
                    onChange={(e) => setEditTemplateItem(prev => ({ ...prev, qty: e.target.value }))} 
                    placeholder="0" 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                    inputMode="decimal"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ціна (з націнкою)</label>
                  <input 
                    type="number" 
                    value={editTemplateItem.price} 
                    onChange={(e) => setEditTemplateItem(prev => ({ ...prev, price: e.target.value }))} 
                    placeholder="0" 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" 
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => { setShowTemplateEditItem(false); setEditTemplateItem({ sectionIdx: null, itemIdx: null, position: '', unit: 'м²', price: '', type: 'Робота' }); }} 
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" 
                style={{ color: '#163248' }}
              >
                Скасувати
              </button>
              <button 
                onClick={() => {
                  if (editTemplateItem.position.trim() && editTemplateItem.sectionIdx !== null && editTemplateItem.itemIdx !== null) {
                    const newSections = [...editingTemplate.sections];
                    newSections[editTemplateItem.sectionIdx].items[editTemplateItem.itemIdx] = {
                      ...newSections[editTemplateItem.sectionIdx].items[editTemplateItem.itemIdx],
                      position: editTemplateItem.position.trim(),
                      unit: editTemplateItem.unit,
                      clientPrice: editingTemplate?._isForContract ? 0 : (parseFloat(editTemplateItem.price) || 0),
                      qty: editingTemplate?._isForContract ? (parseFloat(editTemplateItem.qty) || 0) : (newSections[editTemplateItem.sectionIdx].items[editTemplateItem.itemIdx].qty || 0),
                      type: editTemplateItem.type
                    };
                    setEditingTemplate(prev => ({ ...prev, sections: newSections }));
                    setShowTemplateEditItem(false);
                    setEditTemplateItem({ sectionIdx: null, itemIdx: null, position: '', unit: 'м²', price: '', type: 'Робота' });
                  }
                }} 
                disabled={!editTemplateItem.position.trim()} 
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-opacity ${editTemplateItem.position.trim() ? 'text-white hover:opacity-90' : 'bg-gray-200 text-gray-400'}`}
                style={editTemplateItem.position.trim() ? { backgroundColor: '#163248' } : {}}
              >
                Зберегти
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка підтвердження (універсальна) */}
      {/* Share модалка */}
      {shareModalProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 px-0 sm:px-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl sm:max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={{ color: '#163248' }}>Посилання для клієнта</h3>
              <button onClick={() => { setShareModalProject(null); setShareModalData(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {shareModalLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Завантаження...</p>
              </div>
            ) : shareModalData ? (
              <div className="p-5 space-y-4">
                {/* Посилання */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 truncate border overflow-hidden">
                    {window.location.origin}/share/{shareModalData.share_token}
                  </div>
                  <button 
                    onClick={() => copyShareLink(shareModalData.share_token)}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg flex-shrink-0 transition-colors"
                    style={{ backgroundColor: shareCopied ? '#16a34a' : '#163248' }}
                  >
                    {shareCopied ? '✓ Скопійовано' : 'Копіювати'}
                  </button>
                </div>

                {/* Активне/деактивоване */}
                <div className="flex items-center justify-between py-3 border-t border-b">
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#163248' }}>
                      {shareModalData.is_active !== false ? '✅ Посилання активне' : '⛔ Посилання деактивовано'}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {shareModalData.is_active !== false 
                        ? 'Клієнт може переглядати проєкт'
                        : 'Клієнт побачить повідомлення що доступ закрито'}
                    </p>
                  </div>
                  <button
                    onClick={() => updateShareField('is_active', shareModalData.is_active === false)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${shareModalData.is_active !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${shareModalData.is_active !== false ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>

                {/* Lock/Unlock */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#163248' }}>
                      {shareModalData.is_locked ? '🔒 Зафіксовано' : '🔓 Динамічне оновлення'}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {shareModalData.is_locked 
                        ? 'Клієнт бачить збережену версію'
                        : 'Клієнт бачить актуальні дані'}
                    </p>
                  </div>
                  <button
                    onClick={() => updateShareField('is_locked', !shareModalData.is_locked)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${shareModalData.is_locked ? 'bg-amber-500' : 'bg-green-500'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${shareModalData.is_locked ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>

                {/* Показати попередній */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#163248' }}>
                      Попередній кошторис
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {shareModalData.show_preliminary ? 'Видимий клієнту' : 'Прихований від клієнта'}
                    </p>
                  </div>
                  <button
                    onClick={() => updateShareField('show_preliminary', !shareModalData.show_preliminary)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${shareModalData.show_preliminary ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${shareModalData.show_preliminary ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </button>
                </div>
              </div>
            ) : null}

            {/* Footer */}
            {shareModalData && (
              <div className="px-5 py-3 border-t bg-gray-50 flex justify-between">
                <button
                  onClick={() => {
                    setConfirmModal({
                      show: true,
                      title: 'Видалити посилання?',
                      message: 'Клієнт більше не зможе переглядати проєкт за цим посиланням',
                      onConfirm: deleteShare
                    });
                  }}
                  className="text-sm text-red-500 hover:text-red-600"
                >
                  Видалити посилання
                </button>
                <button
                  onClick={() => { setShareModalProject(null); setShareModalData(null); }}
                  className="text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-gray-100"
                  style={{ color: '#163248' }}
                >
                  Закрити
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl mx-4">
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#163248' }}>{confirmModal.title}</h3>
            <p className="text-sm text-gray-600 mb-6">{confirmModal.message}</p>
            <div className="flex gap-3">
              {confirmModal.onConfirm ? (
                <>
                  <button
                    onClick={() => {
                      const onCancel = confirmModal.onCancel;
                      setConfirmModal({ show: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: null, cancelText: null, confirmStyle: null });
                      if (onCancel) onCancel();
                    }}
                    className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm"
                    style={{ color: '#163248' }}
                  >
                    {confirmModal.cancelText || 'Скасувати'}
                  </button>
                  <button
                    onClick={() => {
                      const onConfirm = confirmModal.onConfirm;
                      setConfirmModal({ show: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: null, cancelText: null, confirmStyle: null });
                      if (onConfirm) onConfirm();
                    }}
                    className="flex-1 px-4 py-2.5 text-white rounded-xl font-medium text-sm"
                    style={{ backgroundColor: confirmModal.confirmStyle === 'brand' ? '#163248' : '#ef4444' }}
                  >
                    {confirmModal.confirmText || 'Видалити'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: null, cancelText: null, confirmStyle: null })}
                  className="flex-1 px-4 py-2.5 text-white rounded-xl font-medium text-sm"
                  style={{ backgroundColor: '#163248' }}
                >
                  {confirmModal.okText || 'Ок'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка довідника */}
      <DirectoryModal
        isOpen={showDirectoryModal}
        onClose={() => setShowDirectoryModal(false)}
        onAddToEstimate={null}
        estimateSections={null}
        templates={templates}
        setTemplates={setTemplates}
        confirmModal={confirmModal}
        setConfirmModal={setConfirmModal}
        branding={brandSettings}
        setPreviewFile={setPreviewFile}
        isPro={isPro}
        showPaywall={showPaywall}
      />

      {/* Покроковий тур */}
      {tourStep > 0 && tourRect && (() => {
        const tourSteps = [
          { target: 'project-card', text: 'Натисніть на картку щоб побачити прогрес і деталі об\'єкту', position: 'right' },
          { target: 'editor-button', text: 'Тут ви відкриваєте редактор кошторисів для роботи з проєктом', position: 'top' },
          { target: 'fab-directory', text: 'Ваш довідник — база позицій і розцінок для швидкого додавання', position: 'top-right' },
          { target: 'fab-templates', text: 'Шаблони — готові набори позицій для типових проєктів', position: 'top-right' },
          { target: 'fab-add', text: 'Створіть новий об\'єкт', position: 'top-right' },
        ];
        const step = tourSteps[tourStep - 1];
        if (!step) return null;
        
        const rect = tourRect;
        const padding = 8;
        
        // Позиція tooltip
        let tooltipStyle = {};
        let arrowClass = '';
        const tooltipWidth = 280;
        
        if (step.position === 'right') {
          tooltipStyle = { 
            top: rect.top + rect.height / 2, 
            left: Math.min(rect.right + 16, window.innerWidth - tooltipWidth - 16),
            transform: 'translateY(-50%)'
          };
          arrowClass = 'left';
        } else if (step.position === 'top') {
          const leftPos = rect.left + rect.width / 2;
          tooltipStyle = { 
            top: rect.top - 16, 
            left: Math.max(16, Math.min(leftPos, window.innerWidth - tooltipWidth / 2 - 16)),
            transform: 'translate(-50%, -100%)'
          };
          arrowClass = 'bottom';
        } else if (step.position === 'top-right') {
          tooltipStyle = { 
            top: rect.top - 16, 
            left: Math.max(16, Math.min(rect.left, window.innerWidth - tooltipWidth - 16)),
            transform: 'translateY(-100%)'
          };
          arrowClass = 'bottom';
        }
        
        return (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 z-[80]" style={{ pointerEvents: 'none' }}>
              <svg className="w-full h-full" style={{ position: 'absolute', inset: 0 }}>
                <defs>
                  <mask id="tour-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect 
                      x={rect.left - padding} y={rect.top - padding} 
                      width={rect.width + padding * 2} height={rect.height + padding * 2} 
                      rx="12" fill="black" 
                    />
                  </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#tour-mask)" />
              </svg>
            </div>
            
            {/* Highlight border */}
            <div 
              className="fixed z-[81] rounded-xl pointer-events-none"
              style={{
                top: rect.top - padding,
                left: rect.left - padding,
                width: rect.width + padding * 2,
                height: rect.height + padding * 2,
                boxShadow: '0 0 0 3px #3b82f6, 0 0 20px rgba(59,130,246,0.3)',
              }}
            />
            
            {/* Tooltip bubble */}
            <div 
              className="fixed z-[82] max-w-xs"
              style={{
                ...tooltipStyle,
                pointerEvents: 'auto'
              }}
            >
              <div 
                className="relative rounded-2xl px-5 py-4 shadow-2xl"
                style={{ backgroundColor: '#163248' }}
              >
                {/* Arrow */}
                {arrowClass === 'left' && (
                  <div 
                    className="absolute"
                    style={{
                      top: '50%', left: -8, transform: 'translateY(-50%)',
                      width: 0, height: 0,
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderRight: '8px solid #163248'
                    }}
                  />
                )}
                {arrowClass === 'bottom' && (
                  <div 
                    className="absolute"
                    style={{
                      bottom: -8, 
                      left: step.position === 'top-right' 
                        ? Math.max(16, rect.left + rect.width / 2 - Math.max(16, Math.min(rect.left, window.innerWidth - 280 - 16)))
                        : '50%', 
                      transform: step.position === 'top-right' ? 'none' : 'translateX(-50%)',
                      width: 0, height: 0,
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderTop: '8px solid #163248'
                    }}
                  />
                )}
                
                <p className="text-sm text-white mb-3">{step.text}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{tourStep} з {tourSteps.length}</span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        setTourStep(0);
                        try { localStorage.setItem('lihtar_tour_projects', 'done'); } catch {}
                      }}
                      className="text-xs text-white/60 hover:text-white/90 transition-colors"
                    >
                      Пропустити
                    </button>
                    <button 
                      onClick={() => {
                        if (tourStep >= tourSteps.length) {
                          setTourStep(0);
                          try { localStorage.setItem('lihtar_tour_projects', 'done'); } catch {}
                        } else {
                          setTourStep(tourStep + 1);
                        }
                      }}
                      className="px-3 py-1 bg-white rounded-lg text-xs font-medium transition-opacity hover:opacity-90"
                      style={{ color: '#163248' }}
                    >
                      {tourStep >= tourSteps.length ? 'Готово' : 'Далі'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        message={paywallMessage}
        trialUsed={trialUsed}
      />
    </div>
  );
};

// ==================== MAIN APP ====================

// Перевірка чи працюємо в Electron
const isElectron = window.electronAPI?.isElectron || false;

// Wrapper: fetch shareData → якщо show_preliminary=true → v2 (dashboard), інакше → v1 (стара).
// ?v2=1 в URL — force override для тестування.
const ShareRouter = ({ shareToken }) => {
  const [showPreliminary, setShowPreliminary] = useState(null); // null = loading
  useEffect(() => {
    // Force override через ?v2=1
    const forceV2 = new URLSearchParams(window.location.search).get('v2') === '1';
    if (forceV2) { setShowPreliminary(true); return; }
    // Інакше: читаємо з бази
    (async () => {
      try {
        const { data } = await supabase
          .from('shared_projects')
          .select('show_preliminary')
          .eq('share_token', shareToken)
          .single();
        setShowPreliminary(data?.show_preliminary === true);
      } catch (e) {
        // Помилка → показуємо стару версію
        setShowPreliminary(false);
      }
    })();
  }, [shareToken]);
  if (showPreliminary === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }
  return showPreliminary
    ? <ClientViewV2 shareToken={shareToken} />
    : <ClientView shareToken={shareToken} />;
};

const App = () => {
  // Перевірка клієнтського посилання
  const shareMatch = window.location.pathname.match(/^\/share\/(.+)$/);
  if (shareMatch) {
    return <ShareRouter shareToken={shareMatch[1]} />;
  }

  // Перевірка картки підряду
  const templateMatch = window.location.pathname.match(/^\/template\/(.+)$/);
  if (templateMatch) {
    return <ContractorView shareToken={templateMatch[1]} />;
  }

  // Перевірка прайс-листа
  const priceMatch = window.location.pathname.match(/^\/price\/(.+)$/);
  if (priceMatch) {
    return <DirectoryPublicView shareToken={priceMatch[1]} />;
  }

  // Гостьовий калькулятор матеріалів
  if (window.location.pathname === "/calculator") {
    return <GuestCalculatorPage />;
  }

  // AI Drawing Reader
  if (window.location.pathname === "/drawing-reader") {
    return <DrawingReader onClose={() => { window.location.href = '/'; }} />;
  }

  return <MainApp />;
};

const MainApp = () => {
  // Стан авторизації
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Стан навігації — відновлюємо з sessionStorage
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem('lihtar_page') || 'projects';
  });
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    const saved = sessionStorage.getItem('lihtar_project_id');
    return saved ? (isNaN(Number(saved)) ? saved : Number(saved)) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'error'
  
  // Підписка
  const { subscription, isPro, isTrial, trialUsed, trialDaysLeft, isFeatureBlocked, isLimitReached, getPaywallMessage } = useSubscription(user?.id);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState('');

  // Активна сесія (multi-tab / multi-device захист)
  const activeSession = useActiveSession(user?.id);
  // Ref для доступу з saveData — щоб не мати activeSession в deps useEffect
  // (інакше кожен render викликав би re-run saveData через новий об'єкт)
  const activeSessionRef = useRef(activeSession);
  useEffect(() => { activeSessionRef.current = activeSession; });

  // Порадник LIHTAR
  const [advisorChatOpen, setAdvisorChatOpen] = useState(false);
  const [showPatchNote, setShowPatchNote] = useState(false);
  const [advisorAnnouncementOpen, setAdvisorAnnouncementOpen] = useState(false);

  // Власні одиниці виміру (per user, LS cache + Supabase)
  const [customUnits, setCustomUnits] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lihtar_custom_units') || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await supabase.from('user_profiles').select('custom_units').eq('user_id', user.id).maybeSingle();
        const arr = Array.isArray(data?.custom_units) ? data.custom_units : [];
        setCustomUnits(arr);
        localStorage.setItem('lihtar_custom_units', JSON.stringify(arr));
      } catch { /* колонка може ще не існувати або нема доступу — залишаємо LS-кеш */ }
    })();
  }, [user?.id]);
  const persistCustomUnits = async (list) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase.from('user_profiles').upsert(
        { user_id: user.id, custom_units: list },
        { onConflict: 'user_id' }
      );
      if (error) throw error;
    } catch {
      // fallback: manual insert/update
      try {
        const { data: existing } = await supabase.from('user_profiles').select('user_id').eq('user_id', user.id).maybeSingle();
        if (existing) {
          await supabase.from('user_profiles').update({ custom_units: list }).eq('user_id', user.id);
        } else {
          await supabase.from('user_profiles').insert({ user_id: user.id, custom_units: list });
        }
      } catch (e2) { console.error('persist custom_units:', e2); }
    }
  };
  const saveCustomUnit = async (unit) => {
    if (!isPro) { showPaywall('custom_units'); return; }
    const trimmed = (unit || '').trim().slice(0, 6);
    if (!trimmed) return;
    if (UNITS.includes(trimmed) || customUnits.includes(trimmed)) return;
    const newList = [...customUnits, trimmed];
    setCustomUnits(newList);
    localStorage.setItem('lihtar_custom_units', JSON.stringify(newList));
    persistCustomUnits(newList);
  };
  const removeCustomUnit = async (unit) => {
    const newList = customUnits.filter(u => u !== unit);
    setCustomUnits(newList);
    localStorage.setItem('lihtar_custom_units', JSON.stringify(newList));
    persistCustomUnits(newList);
  };

  // Показуємо оголошення один раз
  useEffect(() => {
    if (user && !localStorage.getItem('lihtar_advisor_seen')) {
      setTimeout(() => setAdvisorAnnouncementOpen(true), 2000);
    }
  }, [user]);

  const handleAdvisorAnnouncementClose = () => {
    setAdvisorAnnouncementOpen(false);
    localStorage.setItem('lihtar_advisor_seen', '1');
  };

  const showPaywall = (messageKey) => {
    setPaywallMessage(getPaywallMessage(messageKey));
    setPaywallOpen(true);
  };
  
  // Supabase Auth listener
  useEffect(() => {
    // Перевіряємо поточну сесію
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const provider = session.user.app_metadata?.provider || 'email';
        setUser({
          id: session.user.id,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Користувач',
          email: session.user.email,
          avatar: session.user.user_metadata?.avatar_url || null,
          authProvider: provider
        });
      }
      setAuthLoading(false);
    });
    
    // Слухаємо зміни авторизації
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const provider = session.user.app_metadata?.provider || 'email';
        setUser({
          id: session.user.id,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Користувач',
          email: session.user.email,
          avatar: session.user.user_metadata?.avatar_url || null,
          authProvider: provider
        });
        trackEvent(_event === 'SIGNED_IN' ? 'login' : _event === 'SIGNED_UP' ? 'signup' : 'auth', { provider });
        // Google Ads conversion — only on first registration (created < 60 sec ago)
        const createdAt = new Date(session.user.created_at);
        const now = new Date();
        if (now - createdAt < 60000 && typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', { 'send_to': 'AW-17961111255/3doiCM7k5PsbENedw_RC' });
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  // Функції авторизації
  const handleLogin = (userData) => {
    setUser(userData);
  };
  
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCurrentPage('projects');
    setCurrentProjectId(null);
    loadedUserId.current = null;
    sessionStorage.removeItem('lihtar_page');
    sessionStorage.removeItem('lihtar_project_id');
  };
  
  // Шаблони (глобальний стейт)
  const [templates, setTemplates] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [favTemplates, setFavTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lihtar_fav_templates') || '[]'); } catch { return []; }
  });
  const toggleFavTemplate = (id) => {
    setFavTemplates(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem('lihtar_fav_templates', JSON.stringify(next));
      return next;
    });
  };
  const sortedPublicTemplates = [...publicTemplates].sort((a, b) => {
    const aFav = favTemplates.includes(a.id);
    const bFav = favTemplates.includes(b.id);
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return (a.sort_order || 0) - (b.sort_order || 0);
  });
  const [filledCards, setFilledCards] = useState([]); // Заповнені картки підряду
  const [orderCards, setOrderCards] = useState([]); // Картки замовлення
  
  // Завантаження шаблонів з Supabase
  const loadTemplates = async () => {
    if (!user) return [];
    try {
      const { data, error } = await supabase
        .from('templates')
        .select('data')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Помилка завантаження шаблонів:', error);
      }
      return data?.data || [];
    } catch (e) {
      console.error('Помилка завантаження шаблонів:', e);
      return [];
    }
  };
  
  // Завантаження публічних шаблонів LIHTAR
  const loadPublicTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('public_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (!error && data) setPublicTemplates(data);
    } catch (e) {
      console.error('Помилка завантаження публічних шаблонів:', e);
    }
  };

  // Завантаження карток підряду (всі статуси) + кількість відповідей
  const loadFilledCards = async () => {
    if (!user) return [];
    try {
      const { data: cards, error } = await supabase
        .from('shared_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Помилка завантаження карток підряду:', error);
        return [];
      }
      
      if (!cards || cards.length === 0) return [];
      
      // Підтягуємо кількість відповідей для кожної картки
      const ids = cards.map(c => c.id);
      const { data: responses } = await supabase
        .from('contract_responses')
        .select('shared_template_id')
        .in('shared_template_id', ids);
      
      const responseCounts = {};
      (responses || []).forEach(r => {
        responseCounts[r.shared_template_id] = (responseCounts[r.shared_template_id] || 0) + 1;
      });
      
      return cards.map(c => ({ ...c, responsesCount: responseCounts[c.id] || 0 }));
    } catch (e) {
      console.error('Помилка завантаження карток підряду:', e);
      return [];
    }
  };

  // Завантаження карток замовлення
  const loadOrderCards = async () => {
    if (!user) return [];
    try {
      const { data, error } = await supabase
        .from('order_cards')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Помилка завантаження карток замовлення:', error);
        return [];
      }
      return data || [];
    } catch (e) {
      console.error('Помилка завантаження карток замовлення:', e);
      return [];
    }
  };

  // Збереження шаблонів в Supabase
  const saveTemplatesRef = React.useRef(null);
  useEffect(() => {
    if (!user || isLoading) return;
    
    if (saveTemplatesRef.current) clearTimeout(saveTemplatesRef.current);
    saveTemplatesRef.current = setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('templates')
          .upsert({
            user_id: user.id,
            data: templates,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
        
        if (error) console.error('Помилка збереження шаблонів:', error);
      } catch (e) {
        console.error('Помилка збереження шаблонів:', e);
      }
    }, 1000);
    
    return () => {
      if (saveTemplatesRef.current) clearTimeout(saveTemplatesRef.current);
    };
  }, [templates, user, isLoading]);
  
  // Завантаження даних проєктів
  const loadProjects = async () => {
    if (!user) return [];
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('data')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Помилка завантаження з Supabase:', error);
        return [];
      }
      
      if (data && data.length > 0) {
        // Очищаємо localStorage — дані вже в Supabase
        localStorage.removeItem('onero_projects_v3');
        return data.map(row => row.data);
      }
      
      // Supabase пусто — перевіряємо чи це перша міграція
      // Мігруємо тільки якщо ще не мігрували раніше
      const migrated = localStorage.getItem('lihtar_migrated_to_supabase');
      if (!migrated) {
        const saved = localStorage.getItem('onero_projects_v3');
        if (saved) {
          const localProjects = JSON.parse(saved);
          if (localProjects.length > 0) {
            console.log('Мігрую проєкти з localStorage в Supabase...');
            for (const project of localProjects) {
              await supabase.from('projects').insert({
                user_id: user.id,
                data: project
              });
            }
          }
          localStorage.setItem('lihtar_migrated_to_supabase', 'true');
          localStorage.removeItem('onero_projects_v3');
          return localProjects;
        }
      }
      
      return [];
    } catch (e) {
      console.error('Помилка завантаження даних:', e);
      return [];
    }
  };
  
  const [projects, setProjects] = useState([]);

  // Multi-tab detection тепер обробляє useActiveSession (BroadcastChannel + серверний check).
  // Старий multiTabWarning-код прибрано — новий хук замінює його з cross-device підтримкою.

  // Початкове завантаження даних
  useEffect(() => {
    const initializeData = async () => {
      if (!user) return;
      // Пропускаємо якщо той самий юзер (TOKEN_REFRESHED при поверненні на вкладку)
      if (loadedUserId.current === user.id) return;
      loadedUserId.current = user.id;
      setIsLoading(true);
      const [projectsData, templatesData, filledCardsData, orderCardsData] = await Promise.all([
        loadProjects(),
        loadTemplates(),
        loadFilledCards(),
        loadOrderCards(),
        loadPublicTemplates()
      ]);
      setProjects(projectsData);
      setTemplates(templatesData);
      setFilledCards(filledCardsData);
      setOrderCards(orderCardsData);
      dataJustLoaded.current = true;
      setIsLoading(false);
      // Патчноут — показуємо тільки існуючим юзерам (є проєкти або реєстрація до релізу)
      if (!localStorage.getItem('patchnote_sep2026_links_seen')) {
        // Перевіряємо БД: можливо юзер вже відповідав з іншого девайсу/браузера
        const { data: existingFeedback } = await supabase
          .from('user_feedback')
          .select('id')
          .eq('user_id', user.id)
          .in('feedback_type', ['patchnote_sep2026_links', 'patchnote_sep2026_links_skipped'])
          .limit(1);
        if (existingFeedback && existingFeedback.length > 0) {
          localStorage.setItem('patchnote_sep2026_links_seen', 'true');
        } else {
          const regDate = user?.created_at ? new Date(user.created_at) : null;
          const releaseDate = new Date('2026-09-04');
          if ((projectsData && projectsData.length > 0) || (regDate && regDate < releaseDate)) {
            setTimeout(() => setShowPatchNote(true), 1000);
          } else {
            localStorage.setItem('patchnote_sep2026_links_seen', 'true');
          }
        }
      }
    };
    initializeData();
  }, [user]);

  // Зберігання даних в Supabase — тільки поточний проєкт
  useEffect(() => {
    if (isLoading || !user) return;

    const saveData = async () => {
      if (deletingRef.current) return;

      if (dataJustLoaded.current) {
        dataJustLoaded.current = false;
        return;
      }

      // Активна сесія — тільки active може писати в БД
      if (activeSessionRef.current?.isPassive) {
        setSaveStatus('saved'); // м'яко ігноруємо, не показуємо помилку
        return;
      }

      // Визначаємо який проєкт зберігати
      const projectToSave = currentProjectId
        ? projects.find(p => p.id === currentProjectId)
        : null;

      // Якщо немає поточного проєкту — зберігаємо всі (створення/видалення на головній)
      const projectsToSave = projectToSave ? [projectToSave] : projects;

      setSaveStatus('saving');

      try {
        // Отримуємо існуючі проєкти з бази
        const { data: existingRows, error: fetchError } = await supabase
          .from('projects')
          .select('id, data')
          .eq('user_id', user.id);

        if (fetchError) throw fetchError;

        const existingMap = {};
        (existingRows || []).forEach(row => {
          const projectId = row.data?.id;
          if (projectId) existingMap[projectId] = row.id;
        });

        // Видаляємо проєкти яких більше немає (тільки коли на головній сторінці)
        if (!currentProjectId) {
          const currentProjectIds = new Set(projects.map(p => p.id));
          const rowsToDelete = (existingRows || []).filter(row => {
            const projectId = row.data?.id;
            return projectId && !currentProjectIds.has(projectId);
          });
          for (const row of rowsToDelete) {
            await supabase.from('projects').delete().eq('id', row.id);
          }
        }

        // Додаємо/оновлюємо тільки потрібні проєкти
        for (const project of projectsToSave) {
          const projectForStorage = {
            ...project,
            projectPdf: project.projectPdf ? {
              driveFileId: project.projectPdf.driveFileId,
              name: project.projectPdf.name,
              webViewLink: project.projectPdf.webViewLink,
              size: project.projectPdf.size,
              type: project.projectPdf.type
            } : null,
            contractFile: project.contractFile ? {
              driveFileId: project.contractFile.driveFileId,
              name: project.contractFile.name,
              webViewLink: project.contractFile.webViewLink,
              size: project.contractFile.size,
              type: project.contractFile.type
            } : null,
            estimates: project.estimates.map(est => ({
              ...est,
              documents: (est.documents || []).map(doc => ({
                ...doc,
                fileData: undefined
              }))
            }))
          };

          if (existingMap[project.id]) {
            await supabase
              .from('projects')
              .update({
                data: projectForStorage,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingMap[project.id]);
          } else {
            await supabase
              .from('projects')
              .insert({
                user_id: user.id,
                data: projectForStorage
              });
          }
        }

        setSaveStatus('saved');
        // Оновлюємо last_write_at на сервері + перевіряємо чи ми все ще активні
        activeSessionRef.current?.notifyWrite();
      } catch (e) {
        console.error('Помилка збереження в Supabase:', e);
        setSaveStatus('error');
      }
    };

    const timeoutId = setTimeout(saveData, 1000);
    return () => clearTimeout(timeoutId);
  }, [projects, isLoading, user, currentProjectId]);
  
  // Навігація
  const navigateToProjects = () => {
    setCurrentPage('projects');
    setCurrentProjectId(null);
    sessionStorage.setItem('lihtar_page', 'projects');
    sessionStorage.removeItem('lihtar_project_id');
  };
  
  const navigateToProject = (projectId) => {
    setCurrentProjectId(projectId);
    setCurrentPage('estimate');
    sessionStorage.setItem('lihtar_page', 'estimate');
    sessionStorage.setItem('lihtar_project_id', String(projectId));
  };
  
  // CRUD операції для проєктів
  const createProject = (data = {}) => {
    const newProject = createNewProject(data);
    setProjects(prev => [...prev, newProject]);
    trackEvent('project_create', { name: data.name, objectType: data.objectType });
    return newProject;
  };
  
  const deletingRef = React.useRef(false);
  const dataJustLoaded = React.useRef(false);
  const loadedUserId = React.useRef(null);
  
  const deleteProject = async (projectId) => {
    deletingRef.current = true;
    
    // Одразу видаляємо з Supabase
    if (user) {
      try {
        const { data: rows } = await supabase
          .from('projects')
          .select('id, data')
          .eq('user_id', user.id);
        
        const row = (rows || []).find(r => r.data?.id === projectId);
        if (row) {
          const { error } = await supabase.from('projects').delete().eq('id', row.id);
          if (error) console.error('Supabase delete error:', error);
        }
      } catch (e) {
        console.error('Помилка видалення з Supabase:', e);
      }
    }
    
    setProjects(prev => prev.filter(p => p.id !== projectId));
    if (currentProjectId === projectId) {
      navigateToProjects();
    }
    
    // Даємо час щоб useEffect не перезаписав
    setTimeout(() => { deletingRef.current = false; }, 2000);
  };
  
  // Оновлення без зміни порядку (для сторінки об'єктів)
  const updateProject = (projectId, updates) => {
    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, ...updates } : p
    ));
  };
  
  // Оновлення зі зміною порядку (для сторінки звітів)
  const updateProjectWithTimestamp = (projectId, updates) => {
    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
    ));
  };
  
  // Отримати поточний проєкт
  const currentProject = projects.find(p => p.id === currentProjectId);
  
  // Якщо перевіряємо авторизацію - показуємо лоадер
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center">
          <h1 className="text-2xl logo-text mb-4" style={{ letterSpacing: '0.1em' }}>LIHTAR</h1>
          <div className="w-8 h-8 border-3 border-gray-200 rounded-full animate-spin mx-auto" style={{ borderTopColor: '#163248' }}></div>
        </div>
      </div>
    );
  }
  
  // Якщо не авторизований - показуємо Welcome екран
  if (!user) {
    return <LandingPage onLogin={handleLogin} />;
  }
  
  // Loading стан
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <style>{hideSpinnersStyle}</style>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Завантаження...</p>
        </div>
      </div>
    );
  }
  
  // Значення для CustomUnitsContext (спільне для обох return-гілок)
  const customUnitsCtxValue = { units: customUnits, isPro, showPaywall, save: saveCustomUnit, remove: removeCustomUnit };

  // Рендер
  if (currentPage === 'estimate' && currentProject) {
    return (
      <ActiveSessionContext.Provider value={activeSession}>
      <CustomUnitsContext.Provider value={customUnitsCtxValue}>
      <PassiveBanner />
      <ActiveSessionModals />
      <ReadOnlyWrapper>
      <React.Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'#163248',fontFamily:'Roboto,sans-serif'}}>Завантаження...</div>}>
        <EstimateScreen
        project={currentProject}
        allProjects={projects}
        onUpdateProject={(updates) => updateProjectWithTimestamp(currentProjectId, updates)}
        onUpdateOtherProject={updateProject}
        onNavigateBack={navigateToProjects}
        templates={templates}
        setTemplates={setTemplates}
        publicTemplates={sortedPublicTemplates}
        filledCards={filledCards}
        setFilledCards={setFilledCards}
        orderCards={orderCards}
        setOrderCards={setOrderCards}
        user={user}
        onLogout={handleLogout}
        isPro={isPro}
        isTrial={isTrial}
        trialDaysLeft={trialDaysLeft}
        trialUsed={trialUsed}
        onOpenAdvisor={() => setAdvisorChatOpen(true)}
      />
      </React.Suspense>
      </ReadOnlyWrapper>
      <AdvisorChat isOpen={advisorChatOpen} onClose={() => setAdvisorChatOpen(false)} />
      {advisorAnnouncementOpen && !showPatchNote && <AdvisorAnnouncement onClose={handleAdvisorAnnouncementClose} onTry={() => setAdvisorChatOpen(true)} />}
      </CustomUnitsContext.Provider>
      </ActiveSessionContext.Provider>
    );
  }

  return (
    <ActiveSessionContext.Provider value={activeSession}>
    <CustomUnitsContext.Provider value={customUnitsCtxValue}>
    <PassiveBanner />
    <ActiveSessionModals />
    <ReadOnlyWrapper>
    <>
    <ProjectsPage
      projects={projects}
      onSelectProject={navigateToProject}
      onCreateProject={createProject}
      onDeleteProject={deleteProject}
      onUpdateProject={updateProject}
      templates={templates}
      setTemplates={setTemplates}
      publicTemplates={sortedPublicTemplates}
      sortedPublicTemplates={sortedPublicTemplates}
      favTemplates={favTemplates}
      toggleFavTemplate={toggleFavTemplate}
      filledCards={filledCards}
      setFilledCards={setFilledCards}
      orderCards={orderCards}
      setOrderCards={setOrderCards}
      user={user}
      onLogout={handleLogout}
      isPro={isPro}
      isTrial={isTrial}
      trialDaysLeft={trialDaysLeft}
      showPaywall={showPaywall}
      paywallOpen={paywallOpen}
      setPaywallOpen={setPaywallOpen}
      paywallMessage={paywallMessage}
      trialUsed={trialUsed}
    />
    </>
    </ReadOnlyWrapper>
    <AdvisorFAB onClick={() => setAdvisorChatOpen(true)} hasNotification={!localStorage.getItem('lihtar_advisor_seen')} />
    <AdvisorChat isOpen={advisorChatOpen} onClose={() => setAdvisorChatOpen(false)} />
    {advisorAnnouncementOpen && !showPatchNote && <AdvisorAnnouncement onClose={handleAdvisorAnnouncementClose} onTry={() => setAdvisorChatOpen(true)} />}
    {showPatchNote && <PatchNoteModal isOpen={showPatchNote} onClose={() => setShowPatchNote(false)} user={user} />}
    </CustomUnitsContext.Provider>
    </ActiveSessionContext.Provider>
  );
};

export default App;

// ═══ ГОСТЬОВИЙ КАЛЬКУЛЯТОР — сторінка /calculator ═══
const GuestCalculatorPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const openAuth = () => {
    setShowAuthModal(true);
    setAuthMode('register');
    setAuthError('');
    setAuthSuccess('');
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin, scopes: 'https://www.googleapis.com/auth/drive.file' },
      });
      if (error) throw error;
    } catch (err) {
      setLoginLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) { setAuthError('Заповніть всі поля'); return; }
    setEmailLoading(true); setAuthError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message.includes('Invalid login')) setAuthError('Невірний email або пароль');
        else if (error.message.includes('Email not confirmed')) setAuthError('Підтвердіть email — перевірте пошту');
        else setAuthError(error.message);
      }
    } catch { setAuthError('Помилка входу'); }
    setEmailLoading(false);
  };

  const handleEmailRegister = async () => {
    if (!email || !password || !confirmPassword) { setAuthError('Заповніть всі поля'); return; }
    if (password.length < 6) { setAuthError('Пароль має бути не менше 6 символів'); return; }
    if (password !== confirmPassword) { setAuthError('Паролі не збігаються'); return; }
    setEmailLoading(true); setAuthError('');
    try {
      const { error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin } });
      if (error) {
        if (error.message.includes('already registered')) setAuthError('Цей email вже зареєстрований');
        else setAuthError(error.message);
      } else {
        setAuthSuccess('Лист підтвердження надіслано на ' + email + '. Перевірте пошту (і папку "Спам").');
      }
    } catch { setAuthError('Помилка реєстрації'); }
    setEmailLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafbfc', fontFamily: "'Roboto', sans-serif" }}>
      {/* Шапка */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(250,251,252,0.95)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(22,50,72,0.06)',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: 20, color: '#163248', textDecoration: 'none', letterSpacing: 1 }}>LIHTAR</a>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <a href="/" style={{ fontSize: 13, color: '#5a7186', textDecoration: 'none' }}>На головну</a>
            <button
              onClick={openAuth}
              style={{ padding: '8px 18px', borderRadius: 10, border: 'none', background: '#163248', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Зареєструватись
            </button>
          </div>
        </div>
      </nav>

      {/* Заголовок */}
      <div style={{ background: '#163248', padding: '24px 24px 20px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 6px', lineHeight: 1.3 }}>
            Калькулятор будівельних матеріалів
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: 14 }}>
            Стяжка, штукатурка, перегородки, плитка та інше — безкоштовно
          </p>
        </div>
      </div>

      {/* Калькулятор */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 0 40px' }}>
        <MaterialCalculator
          isOpen={true}
          onClose={() => window.location.href = '/'}
          user={null}
          isGuest={true}
          onGuestRegister={openAuth}
          onCreateOrder={() => {}}
        />
      </div>

      {/* Auth модалка */}
      {showAuthModal && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuthModal(false); }}
        >
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', maxWidth: '420px', width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '24px 24px 0', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#163248', marginBottom: '4px' }}>
                {authMode === 'login' ? 'Вхід в LIHTAR' : 'Реєстрація'}
              </div>
              <p style={{ fontSize: '13px', color: '#9ca3af' }}>
                {authMode === 'login' ? 'Оберіть спосіб входу' : 'Створіть акаунт — це безкоштовно'}
              </p>
            </div>
            <div style={{ padding: '20px 24px 24px' }}>
              {authSuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>{authSuccess}</p>
                  <button onClick={() => { setAuthMode('login'); setAuthSuccess(''); }} style={{ marginTop: '16px', padding: '10px 24px', backgroundColor: '#163248', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    Перейти до входу
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={handleGoogleLogin} disabled={loginLoading} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#163248', marginBottom: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loginLoading ? 'Вхід...' : 'Увійти через Google'}
                  </button>
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#166534', lineHeight: '1.5' }}>
                    <strong>🟢 Google акаунт</strong> — файли зберігаються на вашому Google Диску
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 16px' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></div>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>або через email</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '16px', color: '#163248', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none' }}
                      onFocus={(e) => e.target.style.borderColor = '#163248'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Пароль"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '16px', color: '#163248', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none' }}
                      onFocus={(e) => e.target.style.borderColor = '#163248'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
                    {authMode === 'register' && (
                      <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Підтвердіть пароль"
                        style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '16px', color: '#163248', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none' }}
                        onFocus={(e) => e.target.style.borderColor = '#163248'} onBlur={(e) => e.target.style.borderColor = '#e5e7eb'} />
                    )}
                    {authError && <p style={{ fontSize: '13px', color: '#ef4444', textAlign: 'center', margin: 0 }}>{authError}</p>}
                    <button onClick={authMode === 'login' ? handleEmailLogin : handleEmailRegister} disabled={emailLoading}
                      style={{ width: '100%', padding: '12px', backgroundColor: '#163248', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: emailLoading ? 'wait' : 'pointer', opacity: emailLoading ? 0.6 : 1 }}>
                      {emailLoading ? '...' : authMode === 'login' ? 'Увійти' : 'Зареєструватись'}
                    </button>
                  </div>
                  <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', marginTop: '16px' }}>
                    {authMode === 'login' ? (
                      <>Немає акаунту? <span onClick={() => { setAuthMode('register'); setAuthError(''); }} style={{ color: '#163248', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline' }}>Зареєструватись</span></>
                    ) : (
                      <>Вже є акаунт? <span onClick={() => { setAuthMode('login'); setAuthError(''); }} style={{ color: '#163248', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline' }}>Увійти</span></>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
